/**
 * AgentSession - Core abstraction for agent lifecycle and session management.
 *
 * This class is shared between all run modes (interactive, print, rpc).
 * It encapsulates:
 * - Agent state access
 * - Event subscription with automatic session persistence
 * - Model and thinking level management
 * - Compaction (manual and auto)
 * - Bash execution
 * - Session switching and branching
 *
 * Modes use this class and add their own I/O layer on top.
 */

import { readFileSync } from "node:fs";
import { basename, dirname, join } from "node:path";
import type {
	Agent,
	AgentEvent,
	AgentMessage,
	AgentState,
	AgentTool,
	ContextCacheDiagnostics,
	ThinkingLevel,
} from "@apholdings/jensen-agent-core";
import type { AssistantMessage, ImageContent, Message, Model, TextContent } from "@apholdings/jensen-ai";
import {
	isContextOverflow,
	modelsAreEqual,
	resetApiProviders,
	supportsXhigh,
	validateChatCompletionsTranscript,
	validateResponsesTranscript,
} from "@apholdings/jensen-ai";
import { getDocsPath } from "../config.js";
import { theme } from "../modes/interactive/theme/theme.js";
import { stripFrontmatter } from "../utils/frontmatter.js";
import { sleep } from "../utils/sleep.js";
import { type BashResult, executeBash as executeBashCommand, executeBashWithOperations } from "./bash-executor.js";
import { createBtwNextTurnMessage, getBtwNoteFromMessage } from "./btw-command.js";
import type { CavemanLevel } from "./caveman-command.js";
import {
	type CompactionResult,
	calculateContextTokens,
	collectEntriesForBranchSummary,
	compact,
	createDeterministicCompactionDetails,
	estimateContextTokens,
	generateBranchSummary,
	prepareCompaction,
	shouldCompact,
} from "./compaction/index.js";
import {
	type ContextGovernor,
	createMissionContextCheckpoint,
	type EvidenceArchive,
	type FindingRecord,
	type MissionContextCheckpoint,
} from "./context-runtime/index.js";
import { DEFAULT_THINKING_LEVEL } from "./defaults.js";
import {
	buildDelegatedWorkSummary,
	type DelegatedTask,
	type DelegatedWorkSummary,
	extractDelegatedTasks,
	getRecentDelegatedTasks,
	mergeDelegatedTask,
	reconcileDelegatedResult,
} from "./delegated-work.js";
import { exportSessionToHtml, type ToolHtmlRenderer } from "./export-html/index.js";
import { createToolHtmlRenderer } from "./export-html/tool-renderer.js";
import {
	type ContextUsage,
	type ExtensionCommandContextActions,
	type ExtensionContext,
	type ExtensionErrorListener,
	ExtensionRunner,
	type ExtensionUIContext,
	type InputSource,
	type MessageEndEvent,
	type MessageStartEvent,
	type MessageUpdateEvent,
	type SessionBeforeCompactResult,
	type SessionBeforeForkResult,
	type SessionBeforeSwitchResult,
	type SessionBeforeTreeResult,
	type ShutdownHandler,
	type ToolDefinition,
	type ToolExecutionEndEvent,
	type ToolExecutionStartEvent,
	type ToolExecutionUpdateEvent,
	type ToolInfo,
	type TreePreparation,
	type TurnEndEvent,
	type TurnStartEvent,
	wrapRegisteredTools,
} from "./extensions/index.js";
import {
	clearMemoryItems,
	deleteMemoryItem,
	getMemoryItem,
	type MemoryHistorySnapshot,
	type MemoryItem,
	upsertMemoryItems,
} from "./memory.js";
import {
	buildStructuredMemoryCompareData,
	buildStructuredMemoryHistoryData,
	type StructuredMemoryCompareData,
	type StructuredMemoryHistoryData,
} from "./memory-snapshot-contract.js";
import type { BashExecutionMessage, CustomMessage } from "./messages.js";
import type { ModelRegistry } from "./model-registry.js";
import { expandPromptTemplate, type PromptTemplate } from "./prompt-templates.js";
import type { MissionDefinitionInput } from "./reliability/mission-contract-factory.js";
import type { MissionRuntime } from "./reliability/mission-runtime.js";
import { ReliabilitySessionController } from "./reliability/session-controller.js";
import { createWorkspaceVerificationExecutor } from "./reliability/workspace-verification.js";
import type { ResourceExtensionPaths, ResourceLoader } from "./resource-loader.js";
import type {
	BranchSummaryEntry,
	CompactionEntry,
	MemorySnapshotResolution,
	SessionManager,
} from "./session-manager.js";
import { getLatestCompactionEntry } from "./session-manager.js";
import type { SettingsManager } from "./settings-manager.js";
import { BUILTIN_SLASH_COMMANDS, type SlashCommandInfo, type SlashCommandLocation } from "./slash-commands.js";
import { buildSystemPromptRegions } from "./system-prompt.js";
import { TodoEngine } from "./todo/index.js";
import type { BashOperations } from "./tools/bash.js";
import { createAllTools } from "./tools/index.js";
import { createMemoryWriteTool } from "./tools/memory-write.js";
import { createRetrieveEvidenceTool } from "./tools/retrieve-evidence.js";
import {
	createTaskCreateTool,
	createTaskGetTool,
	createTaskListTool,
	createTaskUpdateTool,
	type Task,
} from "./tools/task-tools.js";
import { TodoLoopGuard } from "./tools/todo-loop-guard.js";
import { createTodoReadTool } from "./tools/todo-read.js";
import { createTodoUpdateTool } from "./tools/todo-update.js";
import { createPerTurnLock, createTodoWriteTool, type TodoItem } from "./tools/todo-write.js";
import {
	buildUltraplanPlannerTask,
	buildUltraplanRevisionTask,
	formatUltraplanArtifact,
	parseUltraplanArtifactData,
	parseUltraplanArtifactFromText,
	SESSION_ULTRAPLAN_CUSTOM_TYPE,
	selectUltraplanApplySteps,
	type UltraplanArtifact,
	type UltraplanRunResult,
} from "./ultraplan.js";
import { buildWorkingContext, type WorkingContext } from "./working-context.js";

// ============================================================================
// Caveman Safety Protections (exported for test reference)
// ============================================================================

export const CAVEMAN_SAFETY_PROTECTIONS = [
	"Tool calls, tool arguments, tool results, and command output",
	"JSON, YAML, XML, frontmatter, schemas, or any machine-parsed envelopes",
	"details.results and all result fields used for parent validation",
	"Task, planner, slice, handoff, run, and verification state",
	"Code, file paths, URLs, commit hashes, CLI commands, and exact error messages",
	"Security confirmations, destructive action confirmations, and ordered procedures",
	"Delegated evidence from reviewer, security, pentester, and librarian agents",
] as const;

// ============================================================================
// Skill Block Parsing
// ============================================================================

/** Parsed skill block from a user message */
export interface ParsedSkillBlock {
	name: string;
	location: string;
	content: string;
	userMessage: string | undefined;
}

/**
 * Parse a skill block from message text.
 * Returns null if the text doesn't contain a skill block.
 */
export function parseSkillBlock(text: string): ParsedSkillBlock | null {
	const match = text.match(/^<skill name="([^"]+)" location="([^"]+)">\n([\s\S]*?)\n<\/skill>(?:\n\n([\s\S]+))?$/);
	if (!match) return null;
	return {
		name: match[1],
		location: match[2],
		content: match[3],
		userMessage: match[4]?.trim() || undefined,
	};
}

/** Session-specific events that extend the core AgentEvent */
export type AgentSessionEvent =
	| AgentEvent
	| { type: "auto_compaction_start"; reason: "threshold" | "overflow" }
	| {
			type: "auto_compaction_end";
			result: CompactionResult | undefined;
			aborted: boolean;
			willRetry: boolean;
			errorMessage?: string;
	  }
	| { type: "auto_retry_start"; attempt: number; maxAttempts: number; delayMs: number; errorMessage: string }
	| { type: "auto_retry_end"; success: boolean; attempt: number; finalError?: string }
	| { type: "auto_fallback"; model: Model<any>; fallbackModel: Model<any>; reason: string }
	| { type: "todo_update"; todos: Array<{ content: string; activeForm: string; status: string }> }
	| { type: "memory_update"; items: MemoryItem[] }
	| { type: "task_update"; tasks: Task[] }
	| { type: "notification"; kind: string; message: string };

/** Listener function for agent session events */
export type AgentSessionEventListener = (event: AgentSessionEvent) => void;

// ============================================================================
// Types
// ============================================================================

export interface AgentSessionConfig {
	agent: Agent;
	sessionManager: SessionManager;
	settingsManager: SettingsManager;
	cwd: string;
	/** Models to cycle through with Ctrl+P (from --models flag) */
	scopedModels?: Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }>;
	/** Fallback models to try if the primary model fails */
	fallbackModels?: Model<any>[];
	/** Resource loader for skills, prompts, themes, context files, system prompt */
	resourceLoader: ResourceLoader;
	/** SDK custom tools registered outside extensions */
	customTools?: ToolDefinition[];
	/** Model registry for API key resolution and model discovery */
	modelRegistry: ModelRegistry;
	/** Initial active built-in tool names. Default: [read, bash, edit, write] */
	initialActiveToolNames?: string[];
	/** Override base tools (useful for custom runtimes). */
	baseToolsOverride?: Record<string, AgentTool>;
	/** Cold evidence archive for the model-facing retrieve_evidence capability. */
	evidenceArchive?: EvidenceArchive;
	/** Context Governor for provider-overflow recovery wiring. */
	contextGovernor?: ContextGovernor;
	/** Enable brief-only output contract for this session. Default: false */
	briefOnly?: boolean;
	/** Enable Caveman output compression for this session. Default: "off" */
	cavemanLevel?: CavemanLevel;
	/** Mutable ref used by Agent to access the current ExtensionRunner */
	extensionRunnerRef?: { current?: ExtensionRunner };
}

export interface ExtensionBindings {
	uiContext?: ExtensionUIContext;
	commandContextActions?: ExtensionCommandContextActions;
	shutdownHandler?: ShutdownHandler;
	onError?: ExtensionErrorListener;
}

/** Options for AgentSession.prompt() */
export interface PromptOptions {
	/** Whether to expand file-based prompt templates (default: true) */
	expandPromptTemplates?: boolean;
	/** Image attachments */
	images?: ImageContent[];
	/** When streaming, how to queue the message: "steer" (interrupt) or "followUp" (wait). Required if streaming. */
	streamingBehavior?: "steer" | "followUp";
	/** Source of input for extension input event handlers. Defaults to "interactive". */
	source?: InputSource;
}

/** Result from cycleModel() */
export interface ModelCycleResult {
	model: Model<any>;
	thinkingLevel: ThinkingLevel;
	/** Whether cycling through scoped models (--models flag) or all available */
	isScoped: boolean;
}

/** Session statistics for /session command */
export interface SessionStats {
	sessionFile: string | undefined;
	sessionId: string;
	userMessages: number;
	assistantMessages: number;
	toolCalls: number;
	toolResults: number;
	totalMessages: number;
	tokens: {
		input: number;
		output: number;
		cacheRead: number;
		cacheWrite: number;
		total: number;
	};
	cost: number;
	/** Metadata-only diagnostics for the latest provider request. Prompt content is never included. */
	contextCache?: ContextCacheDiagnostics;
}

// ============================================================================
// Constants
// ============================================================================

/** Standard thinking levels */
const THINKING_LEVELS: ThinkingLevel[] = ["off", "minimal", "low", "medium", "high"];

/** Bounded provider-overflow recovery attempts (Phase P). */
const MAX_OVERFLOW_RECOVERY_ATTEMPTS = 3;

/** Thinking levels including xhigh (for supported models) */
const THINKING_LEVELS_WITH_XHIGH: ThinkingLevel[] = ["off", "minimal", "low", "medium", "high", "xhigh"];

interface UltraplanSubagentTool {
	execute(
		toolCallId: string,
		params: { agent: string; task: string; agentScope?: "user" | "project" | "both" },
		signal: AbortSignal | undefined,
		onUpdate: undefined,
		ctx: ExtensionContext,
	): Promise<{ content: Array<{ type: "text"; text: string }>; isError?: boolean }>;
}

const NO_OP_EXTENSION_UI_CONTEXT: ExtensionUIContext = {
	select: async () => undefined,
	confirm: async () => false,
	input: async () => undefined,
	notify: () => {},
	onTerminalInput: () => () => {},
	setStatus: () => {},
	setWorkingMessage: () => {},
	setWidget: (() => {}) as ExtensionUIContext["setWidget"],
	setFooter: () => {},
	setHeader: () => {},
	setTitle: () => {},
	custom: async () => undefined as never,
	pasteToEditor: () => {},
	setEditorText: () => {},
	getEditorText: () => "",
	editor: async () => undefined,
	setEditorComponent: () => {},
	get theme() {
		return theme;
	},
	getAllThemes: () => [],
	getTheme: () => undefined,
	setTheme: () => ({ success: false, error: "UI unavailable" }),
	getToolsExpanded: () => false,
	setToolsExpanded: () => {},
};

// ============================================================================
// AgentSession Class
// ============================================================================

export class AgentSession {
	readonly agent: Agent;
	readonly sessionManager: SessionManager;
	readonly settingsManager: SettingsManager;

	private _scopedModels: Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }>;
	private _fallbackModels: Model<any>[];
	private _fallbackIndex = -1;

	// Event subscription state
	private _unsubscribeAgent?: () => void;
	private _eventListeners: AgentSessionEventListener[] = [];
	private _agentEventQueue: Promise<void> = Promise.resolve();

	/** Tracks pending steering messages for UI display. Removed when delivered. */
	private _steeringMessages: string[] = [];
	/** Tracks pending follow-up messages for UI display. Removed when delivered. */
	private _followUpMessages: string[] = [];
	/** Messages queued to be included with the next user prompt as context ("asides"). */
	private _pendingNextTurnMessages: CustomMessage[] = [];

	// Compaction state
	private _compactionAbortController: AbortController | undefined = undefined;
	private _autoCompactionAbortController: AbortController | undefined = undefined;
	private _overflowRecoveryCount = 0;

	// Branch summarization state
	private _branchSummaryAbortController: AbortController | undefined = undefined;

	// Retry state
	private _retryAbortController: AbortController | undefined = undefined;
	private _retryAttempt = 0;
	private _retryPromise: Promise<void> | undefined = undefined;
	private _retryResolve: (() => void) | undefined = undefined;

	// Bash execution state
	private _bashAbortController: AbortController | undefined = undefined;
	private _pendingBashMessages: BashExecutionMessage[] = [];

	// Todo state
	private _todos: TodoItem[] = [];
	private _todoRevision = 0;
	private _todoLoopGuard = new TodoLoopGuard();
	private _todoPerTurnLock = createPerTurnLock();
	private _todoReadSnapshot: { revision: number; timestamp: number } | null = null;
	private _todoUpdateRejectionState = { count: 0 };
	private _todoEngine!: TodoEngine;
	private _memoryItems: MemoryItem[] = [];
	private _delegatedTasks: DelegatedTask[] = [];
	private _tasks: Task[] = [];

	// Reliability Kernel (2.2.0): active for normal agent sessions by default.
	private _reliability: ReliabilitySessionController | undefined = undefined;

	// Extension system
	private _extensionRunner: ExtensionRunner | undefined = undefined;
	private _turnIndex = 0;

	private _resourceLoader: ResourceLoader;
	private _customTools: ToolDefinition[];
	private _baseToolRegistry: Map<string, AgentTool> = new Map();
	private _cwd: string;
	private _extensionRunnerRef?: { current?: ExtensionRunner };
	private _initialActiveToolNames?: string[];
	private _baseToolsOverride?: Record<string, AgentTool>;
	private _evidenceArchive?: EvidenceArchive;
	private _extensionUIContext?: ExtensionUIContext;
	private _extensionCommandContextActions?: ExtensionCommandContextActions;
	private _extensionShutdownHandler?: ShutdownHandler;
	private _extensionErrorListener?: ExtensionErrorListener;
	private _extensionErrorUnsubscriber?: () => void;
	private _contextGovernor?: ContextGovernor;

	// Model registry for API key resolution
	private _modelRegistry: ModelRegistry;

	// Tool registry for extension getTools/setTools
	private _toolRegistry: Map<string, AgentTool> = new Map();
	private _toolPromptSnippets: Map<string, string> = new Map();
	private _toolPromptGuidelines: Map<string, string[]> = new Map();

	// Base system prompt (without extension appends) - used to apply fresh appends each turn
	private _baseSystemPrompt = "";
	private _baseDynamicPrompt = "";
	private _briefOnly = false;
	private _cavemanLevel: CavemanLevel = "off";

	constructor(config: AgentSessionConfig) {
		this.agent = config.agent;
		this.sessionManager = config.sessionManager;
		this.settingsManager = config.settingsManager;
		this._scopedModels = config.scopedModels ?? [];
		this._fallbackModels = config.fallbackModels ?? [];
		this._resourceLoader = config.resourceLoader;
		this._customTools = config.customTools ?? [];
		this._cwd = config.cwd;
		this._modelRegistry = config.modelRegistry;
		this._extensionRunnerRef = config.extensionRunnerRef;
		this._initialActiveToolNames = config.initialActiveToolNames;
		this._briefOnly = config.briefOnly ?? false;
		this._cavemanLevel = config.cavemanLevel ?? "off";
		this._baseToolsOverride = config.baseToolsOverride;
		this._evidenceArchive = config.evidenceArchive;
		this._contextGovernor = config.contextGovernor;

		// Reliability Kernel (2.2.0): initialize before tool hooks so the bridge is
		// authoritative for every tool call and turn end. Restores a persisted
		// mission on resume, otherwise starts in governance mode.
		this._reliability = new ReliabilitySessionController({
			cwd: this._cwd,
			sessionManager: this.sessionManager,
			verificationExecutor: createWorkspaceVerificationExecutor({ cwd: this._cwd }),
			getTools: () => this.agent.state.tools,
		});

		// Always subscribe to agent events for internal handling
		// (session persistence, extensions, auto-compaction, retry logic)
		this._unsubscribeAgent = this.agent.subscribe(this._handleAgentEvent);
		this._installAgentToolHooks();
		this._installReliabilityTurnEndHook();

		const restoredContext = this.sessionManager.buildSessionContext();
		this._todos = restoredContext.todos as TodoItem[];
		this._resetTodoEngine();
		this._memoryItems = restoredContext.memoryItems;
		this._tasks = this.sessionManager.getLatestSessionTasks();

		this._buildRuntime({
			activeToolNames: this._initialActiveToolNames,
			includeAllExtensionTools: true,
		});
	}

	/** Model registry for API key resolution and model discovery */
	get modelRegistry(): ModelRegistry {
		return this._modelRegistry;
	}

	/**
	 * Install tool hooks once on the Agent instance.
	 *
	 * The callbacks read `this._extensionRunner` at execution time, so extension reload swaps in the
	 * new runner without reinstalling hooks. Extension-specific tool wrappers are still used to adapt
	 * registered tool execution to the extension context. Tool call and tool result interception now
	 * happens here instead of in wrappers.
	 */
	private _installAgentToolHooks(): void {
		this.agent.setBeforeToolCall(async ({ toolCall, args }) => {
			// Reliability Kernel (2.2.0): validate the requested action against the
			// tool registry and mission policy before execution. Invalid actions are
			// blocked and returned as structured failures to the model.
			const reliability = this._reliability;
			if (reliability) {
				const decision = await reliability.beforeToolCall(toolCall);
				if (decision.block) {
					return { block: true, reason: decision.reason };
				}
			}

			const runner = this._extensionRunner;
			if (!runner?.hasHandlers("tool_call")) {
				return undefined;
			}

			await this._agentEventQueue;

			try {
				return await runner.emitToolCall({
					type: "tool_call",
					toolName: toolCall.name,
					toolCallId: toolCall.id,
					input: args as Record<string, unknown>,
				});
			} catch (err) {
				if (err instanceof Error) {
					throw err;
				}
				throw new Error(`Extension failed, blocking execution: ${String(err)}`);
			}
		});

		this.agent.setAfterToolCall(async ({ toolCall, args, result, isError }) => {
			// Reliability Kernel (2.2.0): record the observed real tool outcome as
			// authoritative evidence before any extension override is applied.
			this._reliability?.afterToolCall(toolCall, isError);

			if (toolCall.name === "todo_write" && !isError) {
				const details = result.details as Record<string, unknown> | undefined;
				if (details?.changed === true || details?.todoWriteAlreadyApplied === true) {
					this._todoPerTurnLock.setLockedForCurrentTurn();
					this.setActiveToolsByName(this.getActiveToolNames().filter((name) => name !== "todo_write"));
				}
			}

			const runner = this._extensionRunner;
			if (!runner?.hasHandlers("tool_result")) {
				return undefined;
			}

			const hookResult = await runner.emitToolResult({
				type: "tool_result",
				toolName: toolCall.name,
				toolCallId: toolCall.id,
				input: args as Record<string, unknown>,
				content: result.content,
				details: isError ? undefined : result.details,
				isError,
			});

			if (!hookResult || isError) {
				return undefined;
			}

			return {
				content: hookResult.content,
				details: hookResult.details,
			};
		});
	}

	/**
	 * Install the Reliability Kernel turn-end hook.
	 *
	 * When a mission is active, "the model finished its turn without further tool
	 * calls" is routed through the Completion Gate. A rejected finalization keeps
	 * the run alive with a structured FINALIZATION_REJECTED message so the model
	 * gets another bounded turn.
	 */
	private _installReliabilityTurnEndHook(): void {
		this.agent.setOnTurnEnd(async () => {
			if (!this._reliability) return undefined;
			const result = await this._reliability.onTurnEnd();
			return result.continue ? { continue: true, message: result.message } : { continue: false };
		});
	}

	// =========================================================================
	// Reliability Kernel public API
	// =========================================================================

	/** Whether the Reliability Kernel has an active (or restored) mission. */
	get reliabilityActive(): boolean {
		return this._reliability?.isActive ?? false;
	}

	/** Active mission id, if any. */
	get missionId(): string | undefined {
		return this._reliability?.missionId;
	}

	/** Active mission phase, if any. */
	get reliabilityPhase(): ReliabilitySessionController["phase"] {
		return this._reliability?.phase;
	}

	/** Read-only access to the active MissionRuntime (for tooling/tests). */
	get reliabilityRuntime(): MissionRuntime | undefined {
		return this._reliability?.runtime;
	}

	/** True when a persisted reliability document failed to deserialize (kept intact). */
	get corruptReliabilityState(): boolean {
		return this._reliability?.corruptPersistedState ?? false;
	}

	/** Current acceptance-criterion view for the active mission. */
	getReliabilityCriteria(): ReturnType<ReliabilitySessionController["criterionView"]> {
		return this._reliability?.criterionView() ?? [];
	}

	/** Compact authoritative mission state for model context, if active. */
	getMissionSummary(): string | undefined {
		return this._reliability?.summarizeForModel();
	}

	/**
	 * Build a durable-projection mission context checkpoint (L2 warm memory).
	 *
	 * This is a bounded, deterministic projection over already-durable state —
	 * the Reliability Kernel mission (objective/constraints/criteria/failures),
	 * session memory, todos, tasks, and latest compaction file operations. It is
	 * used by the Context Governor to rehydrate operational state across a
	 * rollover and never carries completion authority.
	 */
	buildMissionContextCheckpoint(
		evidenceRefs: { evidenceId: string; summary: string }[] = [],
	): MissionContextCheckpoint | undefined {
		const runtime = this._reliability?.runtime;
		const missionId = this._reliability?.missionId ?? this.sessionManager.getSessionId();
		if (!missionId) return undefined;

		const constraints: string[] = [];
		if (runtime) {
			for (const c of runtime.constraints) constraints.push(`${c.id}: ${c.statement}`);
		}

		const completedSteps: string[] = [];
		const pendingSteps: string[] = [];
		for (const todo of this._todos) {
			const text = todo.status === "in_progress" ? todo.activeForm : todo.content;
			if (todo.status === "completed") completedSteps.push(todo.content);
			else pendingSteps.push(text);
		}

		const nextActions: string[] = [];
		for (const task of this._tasks) {
			if (task.status === "in_progress") nextActions.push(task.activeForm ?? task.subject);
			else if (task.status === "pending") nextActions.push(task.subject);
		}

		// Bounded: keep the checkpoint compact by construction.
		const findings: FindingRecord[] = this._memoryItems.slice(0, 24).map((item) => ({
			subject: item.key,
			detail: item.value,
		}));

		const activeFiles = this._latestCompactionFileLists();

		const blockers: string[] = [];
		if (runtime) {
			for (const failure of runtime.failures.slice(-8)) {
				if (failure.category === "CONTEXT_REQUIRED" || failure.category === "FINALIZATION_REJECTED") {
					blockers.push(failure.message);
				}
			}
		}

		return createMissionContextCheckpoint(missionId, {
			objective: runtime?.goal ?? this.getMissionSummary() ?? "",
			constraints,
			decisions: [],
			plan: "",
			completedSteps,
			pendingSteps,
			activeFiles,
			findings,
			evidenceRefs: evidenceRefs.slice(-24),
			testState: {},
			blockers,
			nextActions: nextActions.slice(0, 12),
		});
	}

	private _latestCompactionFileLists(): string[] {
		const branch = this.sessionManager.getBranch();
		const latest = getLatestCompactionEntry(branch);
		if (!latest) return [];
		const details = latest.details as { readFiles?: string[]; modifiedFiles?: string[] } | undefined;
		const files = new Set<string>();
		for (const f of details?.readFiles ?? []) files.add(f);
		for (const f of details?.modifiedFiles ?? []) files.add(f);
		return [...files].slice(0, 64);
	}

	/** Start (or replace) the governed mission for this session. */
	startMission(definition: MissionDefinitionInput): void {
		this._reliability?.startMission(definition);
		this._applyReliabilityContext();
	}

	/** Inject the current authoritative mission state into the next model turn. */
	private _applyReliabilityContext(): void {
		if (this._reliability?.isActive) {
			this.agent.setDynamicPrompt(this._reliability.summarizeForModel());
		}
	}

	// =========================================================================
	// Event Subscription
	// =========================================================================

	/** Emit an event to all listeners */
	private _emit(event: AgentSessionEvent): void {
		for (const l of this._eventListeners) {
			l(event);
		}
	}

	// Track last assistant message for auto-compaction check
	private _lastAssistantMessage: AssistantMessage | undefined = undefined;

	/** Internal handler for agent events - shared by subscribe and reconnect */
	private _handleAgentEvent = (event: AgentEvent): void => {
		// Create retry promise synchronously before queueing async processing.
		// Agent.emit() calls this handler synchronously, and prompt() calls waitForRetry()
		// as soon as agent.prompt() resolves. If _retryPromise is created only inside
		// _processAgentEvent, slow earlier queued events can delay agent_end processing
		// and waitForRetry() can miss the in-flight retry.
		this._createRetryPromiseForAgentEnd(event);

		this._agentEventQueue = this._agentEventQueue.then(
			() => this._processAgentEvent(event),
			() => this._processAgentEvent(event),
		);

		// Keep queue alive if an event handler fails
		this._agentEventQueue.catch(() => {});
	};

	private _createRetryPromiseForAgentEnd(event: AgentEvent): void {
		if (event.type !== "agent_end" || this._retryPromise) {
			return;
		}

		const settings = this.settingsManager.getRetrySettings();
		if (!settings.enabled) {
			return;
		}

		const lastAssistant = this._findLastAssistantInMessages(event.messages);
		if (!lastAssistant || !this._isRetryableError(lastAssistant)) {
			return;
		}

		this._retryPromise = new Promise((resolve) => {
			this._retryResolve = resolve;
		});
	}

	private _findLastAssistantInMessages(messages: AgentMessage[]): AssistantMessage | undefined {
		for (let i = messages.length - 1; i >= 0; i--) {
			const message = messages[i];
			if (message.role === "assistant") {
				return message as AssistantMessage;
			}
		}
		return undefined;
	}

	private async _processAgentEvent(event: AgentEvent): Promise<void> {
		// When a user message starts, check if it's from either queue and remove it BEFORE emitting
		// This ensures the UI sees the updated queue state
		if (event.type === "message_start" && event.message.role === "user") {
			this._overflowRecoveryCount = 0;
			const messageText = this._getUserMessageText(event.message);
			if (messageText) {
				// Check steering queue first
				const steeringIndex = this._steeringMessages.indexOf(messageText);
				if (steeringIndex !== -1) {
					this._steeringMessages.splice(steeringIndex, 1);
				} else {
					// Check follow-up queue
					const followUpIndex = this._followUpMessages.indexOf(messageText);
					if (followUpIndex !== -1) {
						this._followUpMessages.splice(followUpIndex, 1);
					}
				}
			}
		}

		// Emit to extensions first
		await this._emitExtensionEvent(event);
		this._updateDelegatedWorkState(event);

		// Notify all listeners
		this._emit(event);

		// Handle session persistence
		if (event.type === "message_end") {
			if (event.message.role === "user") {
				this._todoLoopGuard.resetOnNewUserMessage();
				this._todoUpdateRejectionState.count = 0;
				this._todoPerTurnLock.currentTurn++;
				this._todoPerTurnLock.lockedUntil = -1;
				this._todoPerTurnLock.reservedUntil = -1;
			}
			// Check if this is a custom message from extensions
			if (event.message.role === "custom") {
				// Persist as CustomMessageEntry
				this.sessionManager.appendCustomMessageEntry(
					event.message.customType,
					event.message.content,
					event.message.display,
					event.message.details,
				);
			} else if (
				event.message.role === "user" ||
				event.message.role === "assistant" ||
				event.message.role === "toolResult"
			) {
				// Regular LLM message - persist as SessionMessageEntry
				this.sessionManager.appendMessage(event.message);
			}
			// Other message types (bashExecution, compactionSummary, branchSummary) are persisted elsewhere

			// Track assistant message for auto-compaction (checked on agent_end)
			if (event.message.role === "assistant") {
				this._lastAssistantMessage = event.message;

				const assistantMsg = event.message as AssistantMessage;
				if (assistantMsg.stopReason !== "error") {
					this._overflowRecoveryCount = 0;

					// Feed authoritative provider usage back into the governor for
					// deterministic conservative calibration (only ever raises the
					// estimate, never lowers it).
					const governor = this._contextGovernor;
					const diagnostics = governor?.lastDiagnostics;
					if (governor && diagnostics && assistantMsg.usage) {
						const observed = assistantMsg.usage.input + assistantMsg.usage.cacheRead;
						if (observed > 0 && diagnostics.inputTokensAfter > 0) {
							governor.observeProviderUsage(diagnostics.inputTokensAfter, observed);
						}
					}
				}

				// Reset retry counter immediately on successful assistant response
				// This prevents accumulation across multiple LLM calls within a turn
				if (assistantMsg.stopReason !== "error" && this._retryAttempt > 0) {
					this._emit({
						type: "auto_retry_end",
						success: true,
						attempt: this._retryAttempt,
					});
					this._retryAttempt = 0;
					this._resolveRetry();
				}
			}

			// Final wire-payload validation after every context transformation.
			// Runs on the exact serialized message array just before network transport.
			// Invalid transcripts fail locally with INVALID_TOOL_TRANSCRIPT and produce
			// zero network requests, without automatic retry.
			if (event.message.role === "assistant" || event.message.role === "toolResult") {
				const model = this.agent.state.model;
				if (model) {
					const history = this.agent.state.messages as Message[];
					const useResponses =
						model.api === "openai-responses" ||
						model.api === "azure-openai-responses" ||
						model.api === "openai-codex-responses";
					const result = useResponses
						? validateResponsesTranscript(history, model.provider, model.id)
						: validateChatCompletionsTranscript(history, model.provider, model.id);
					if ("code" in result) {
						throw new Error(
							`INVALID_TOOL_TRANSCRIPT: protocol=${result.protocol}, ` +
								`missingToolCallIds=[${result.missingToolCallIds.join(", ")}], ` +
								`duplicateToolCallIds=[${result.duplicateToolCallIds.join(", ")}], ` +
								`orphanToolResultIds=[${result.orphanToolResultIds.join(", ")}], ` +
								`duplicateCallIds=[${result.duplicateCallIds.join(", ")}], ` +
								`provider=${result.provider}, model=${result.model}, messageIndex=${result.messageIndex}`,
						);
					}
				}
			}
		}

		// Check auto-retry and auto-compaction after agent completes
		if (event.type === "agent_end" && this._lastAssistantMessage) {
			const msg = this._lastAssistantMessage;
			this._lastAssistantMessage = undefined;

			// Check for retryable errors first (overloaded, rate limit, server errors)
			if (this._isRetryableError(msg)) {
				const didRetry = await this._handleRetryableError(msg);
				if (didRetry) return; // Retry was initiated, don't proceed to compaction
			} else if (msg.stopReason === "error" && msg.errorMessage) {
				const contextWindow = this.model?.contextWindow ?? 0;
				if (!isContextOverflow(msg, contextWindow)) {
					const didFallback = await this._handleFallback(msg, msg.errorMessage);
					if (didFallback) return;
				}
			}

			await this._checkCompaction(msg);
		}
	}

	/** Resolve the pending retry promise */
	private _resolveRetry(): void {
		if (this._retryResolve) {
			this._retryResolve();
			this._retryResolve = undefined;
			this._retryPromise = undefined;
		}
	}

	/** Extract text content from a message */
	private _getUserMessageText(message: Message): string {
		if (message.role !== "user") return "";
		const content = message.content;
		if (typeof content === "string") return content;
		const textBlocks = content.filter((c) => c.type === "text");
		return textBlocks.map((c) => (c as TextContent).text).join("");
	}

	/** Find the last assistant message in agent state (including aborted ones) */
	private _findLastAssistantMessage(): AssistantMessage | undefined {
		const messages = this.agent.state.messages;
		for (let i = messages.length - 1; i >= 0; i--) {
			const msg = messages[i];
			if (msg.role === "assistant") {
				return msg as AssistantMessage;
			}
		}
		return undefined;
	}

	/** Emit extension events based on agent events */
	private async _emitExtensionEvent(event: AgentEvent): Promise<void> {
		if (!this._extensionRunner) return;

		if (event.type === "agent_start") {
			this._turnIndex = 0;
			await this._extensionRunner.emit({ type: "agent_start" });
		} else if (event.type === "agent_end") {
			await this._extensionRunner.emit({ type: "agent_end", messages: event.messages });
		} else if (event.type === "turn_start") {
			const extensionEvent: TurnStartEvent = {
				type: "turn_start",
				turnIndex: this._turnIndex,
				timestamp: Date.now(),
			};
			await this._extensionRunner.emit(extensionEvent);
		} else if (event.type === "turn_end") {
			const extensionEvent: TurnEndEvent = {
				type: "turn_end",
				turnIndex: this._turnIndex,
				message: event.message,
				toolResults: event.toolResults,
			};
			await this._extensionRunner.emit(extensionEvent);
			this._turnIndex++;
		} else if (event.type === "message_start") {
			const extensionEvent: MessageStartEvent = {
				type: "message_start",
				message: event.message,
			};
			await this._extensionRunner.emit(extensionEvent);
		} else if (event.type === "message_update") {
			const extensionEvent: MessageUpdateEvent = {
				type: "message_update",
				message: event.message,
				assistantMessageEvent: event.assistantMessageEvent,
			};
			await this._extensionRunner.emit(extensionEvent);
		} else if (event.type === "message_end") {
			const extensionEvent: MessageEndEvent = {
				type: "message_end",
				message: event.message,
			};
			await this._extensionRunner.emit(extensionEvent);
		} else if (event.type === "tool_execution_start") {
			const extensionEvent: ToolExecutionStartEvent = {
				type: "tool_execution_start",
				toolCallId: event.toolCallId,
				toolName: event.toolName,
				args: event.args,
			};
			await this._extensionRunner.emit(extensionEvent);
		} else if (event.type === "tool_execution_update") {
			const extensionEvent: ToolExecutionUpdateEvent = {
				type: "tool_execution_update",
				toolCallId: event.toolCallId,
				toolName: event.toolName,
				args: event.args,
				partialResult: event.partialResult,
			};
			await this._extensionRunner.emit(extensionEvent);
		} else if (event.type === "tool_execution_end") {
			if (event.toolName === "todo_write") {
				const details = event.result.details as Record<string, unknown> | undefined;

				if (details?.changed) {
					// Successful state-mutating todo_write — hide from future provider
					// requests for the rest of this user turn.
					this._todoPerTurnLock.setLockedForCurrentTurn();
					const activeNames = this.getActiveToolNames().filter((n) => n !== "todo_write");
					this.setActiveToolsByName(activeNames);
				} else if (details?.todoWriteAlreadyApplied) {
					// First duplicate rejection — keep todo_write hidden for the rest of
					// the turn so the provider cannot call it again.
					this._todoPerTurnLock.setLockedForCurrentTurn();
					const activeNames = this.getActiveToolNames().filter((n) => n !== "todo_write");
					this.setActiveToolsByName(activeNames);
				}
			}
			if (!event.isError && event.toolName !== "todo_write" && event.toolName !== "todo_read") {
				this._todoLoopGuard.resetOnNonTodoToolSuccess(event.toolName);
				this._todoEngine.recordProgress();
			}
			const extensionEvent: ToolExecutionEndEvent = {
				type: "tool_execution_end",
				toolCallId: event.toolCallId,
				toolName: event.toolName,
				result: event.result,
				isError: event.isError,
			};
			await this._extensionRunner.emit(extensionEvent);
		}
	}

	private _updateDelegatedWorkState(event: AgentEvent): void {
		if (event.type === "tool_execution_start") {
			const tasks = extractDelegatedTasks({
				type: "tool_call",
				toolCallId: event.toolCallId,
				toolName: event.toolName,
				input: typeof event.args === "object" && event.args !== null ? event.args : {},
			});
			for (const task of tasks) {
				this._delegatedTasks = mergeDelegatedTask(this._delegatedTasks, task);
			}

			// Operator state discipline advisory: warn if delegating without visible task/todo state
			if (event.toolName === "subagent") {
				const hasVisibleState = this._tasks.length > 0 || this._todos.length > 0;
				if (!hasVisibleState) {
					this._emit({
						type: "notification",
						kind: "operator_discipline_advisory",
						message:
							"Delegating without visible task or todo state. Consider creating tracking entries before delegating.",
					});
				}
			}

			return;
		}

		if (
			(event.type !== "tool_execution_update" && event.type !== "tool_execution_end") ||
			event.toolName !== "subagent"
		) {
			return;
		}

		const matchingTasks = this._delegatedTasks.filter((task) => task.toolCallId === event.toolCallId);
		if (matchingTasks.length === 0) {
			return;
		}

		const resultPayload = event.type === "tool_execution_end" ? event.result : event.partialResult;
		const toolResult = {
			role: "toolResult",
			toolCallId: event.toolCallId,
			toolName: event.toolName,
			content:
				resultPayload && typeof resultPayload === "object" && Array.isArray(resultPayload.content)
					? resultPayload.content
					: [],
			details: resultPayload && typeof resultPayload === "object" ? resultPayload.details : undefined,
			isError: event.type === "tool_execution_end" ? event.isError : false,
			timestamp: Date.now(),
		} as const;

		this._delegatedTasks = reconcileDelegatedResult(this._delegatedTasks, toolResult);
	}

	/**
	 * Subscribe to agent events.
	 * Session persistence is handled internally (saves messages on message_end).
	 * Multiple listeners can be added. Returns unsubscribe function for this listener.
	 */
	subscribe(listener: AgentSessionEventListener): () => void {
		this._eventListeners.push(listener);

		// Return unsubscribe function for this specific listener
		return () => {
			const index = this._eventListeners.indexOf(listener);
			if (index !== -1) {
				this._eventListeners.splice(index, 1);
			}
		};
	}

	/**
	 * Temporarily disconnect from agent events.
	 * User listeners are preserved and will receive events again after resubscribe().
	 * Used internally during operations that need to pause event processing.
	 */
	private _disconnectFromAgent(): void {
		if (this._unsubscribeAgent) {
			this._unsubscribeAgent();
			this._unsubscribeAgent = undefined;
		}
	}

	/**
	 * Reconnect to agent events after _disconnectFromAgent().
	 * Preserves all existing listeners.
	 */
	private _reconnectToAgent(): void {
		if (this._unsubscribeAgent) return; // Already connected
		this._unsubscribeAgent = this.agent.subscribe(this._handleAgentEvent);
	}

	/**
	 * Remove all listeners and disconnect from agent.
	 * Call this when completely done with the session.
	 */
	dispose(): void {
		this._disconnectFromAgent();
		this._eventListeners = [];
	}

	// =========================================================================
	// Read-only State Access
	// =========================================================================

	/** Full agent state */
	get state(): AgentState {
		return this.agent.state;
	}

	/** Current model (may be undefined if not yet selected) */
	get model(): Model<any> | undefined {
		return this.agent.state.model;
	}

	/** Current thinking level */
	get thinkingLevel(): ThinkingLevel {
		return this.agent.state.thinkingLevel;
	}

	/** Whether agent is currently streaming a response */
	get isStreaming(): boolean {
		return this.agent.state.isStreaming;
	}

	/** Current effective system prompt (includes any per-turn extension modifications) */
	get systemPrompt(): string {
		const { systemPrompt, dynamicPrompt } = this.agent.state;
		return dynamicPrompt ? `${systemPrompt}\n\n${dynamicPrompt}` : systemPrompt;
	}

	/** Current retry attempt (0 if not retrying) */
	get retryAttempt(): number {
		return this._retryAttempt;
	}

	/** Whether the brief-only output contract is enabled for this session runtime. */
	get briefOnly(): boolean {
		return this._briefOnly;
	}

	/** Current Caveman output compression level for this session runtime. */
	get cavemanLevel(): CavemanLevel {
		return this._cavemanLevel;
	}

	/** Pending /btw guidance notes that will be injected on the next turn only. */
	getPendingByTheWayNotes(): readonly string[] {
		return this._pendingNextTurnMessages
			.map((message) => getBtwNoteFromMessage(message))
			.filter((note): note is string => note !== undefined);
	}

	/**
	 * Get the names of currently active tools.
	 * Returns the names of tools currently set on the agent.
	 */
	getActiveToolNames(): string[] {
		return this.agent.state.tools.map((t) => t.name);
	}

	/**
	 * Get all configured tools with name, description, and parameter schema.
	 */
	getAllTools(): ToolInfo[] {
		return Array.from(this._toolRegistry.values()).map((t) => ({
			name: t.name,
			description: t.description,
			parameters: t.parameters,
		}));
	}

	/**
	 * Set active tools by name.
	 * Only tools in the registry can be enabled. Unknown tool names are ignored.
	 * Also rebuilds the system prompt to reflect the new tool set.
	 * Changes take effect on the next agent turn.
	 */
	setActiveToolsByName(toolNames: string[]): void {
		const tools: AgentTool[] = [];
		const validToolNames: string[] = [];
		for (const name of toolNames) {
			const tool = this._toolRegistry.get(name);
			if (tool) {
				tools.push(tool);
				validToolNames.push(name);
			}
		}
		this.agent.setTools(tools);

		// Rebuild base system prompt with new tool set
		this._baseSystemPrompt = this._rebuildSystemPrompt(validToolNames);
		this._applyBaseSystemPrompt();
	}

	/**
	 * Get the current todo list.
	 * Returns the session's todo state for UI rendering.
	 */
	getTodos(): ReadonlyArray<TodoItem> {
		return this._todos;
	}

	/**
	 * Live TODO subsystem diagnostics (progress epoch, snapshot/ledger counts,
	 * loop-chain state). Read-only, used by operability surfaces.
	 */
	getTodoDiagnostics() {
		return this._todoEngine.getDiagnostics();
	}

	private _resetTodoEngine(): void {
		this._todoEngine = new TodoEngine(`session:${this.sessionManager.getSessionId()}`, {}, Date.now, {
			load: () => this.sessionManager.getLatestTodoEngineState(),
			save: (state) => {
				this.sessionManager.appendTodoEngineState(state);
			},
		});
		this._todoRevision = this._todoEngine.getCurrentRevision();
		if (this._todoRevision === 0 && this._todos.length > 0) {
			this._todoEngine.recordState(0, this._todos);
		}
	}

	/**
	 * Get the current todo revision number.
	 * Increments on every non-no-op todo_write or todo_update mutation.
	 */
	get todoRevision(): number {
		return this._todoRevision;
	}

	getDelegatedTasks(): readonly DelegatedTask[] {
		return this._delegatedTasks;
	}

	getDelegatedWorkSummary(): DelegatedWorkSummary {
		return buildDelegatedWorkSummary(this._delegatedTasks);
	}

	getRecentDelegatedTasks(limit = 3): readonly DelegatedTask[] {
		return getRecentDelegatedTasks(this._delegatedTasks, { limit });
	}

	/**
	 * Internal: Set todos and emit update event.
	 * Called by the todo_write tool to update session state.
	 */
	private _setTodos(todos: TodoItem[]): void {
		// Ensure all todos have IDs before persisting
		const withIds: {
			id: string;
			content: string;
			activeForm: string;
			status: "pending" | "in_progress" | "completed";
		}[] = todos.map((t) => ({
			id: t.id || crypto.randomUUID(),
			content: t.content,
			activeForm: t.activeForm,
			status: t.status,
		}));
		this._todos = withIds;
		this._todoRevision++;
		this._todoEngine.recordState(this._todoRevision, this._todos);
		// Invalidate read snapshot - external revision changed, any cached read is stale
		this._todoReadSnapshot = null;
		this.sessionManager.appendSessionTodos(withIds);
		this._emit({ type: "todo_update", todos: this._todos });
	}

	getMemoryItems(): readonly MemoryItem[] {
		return this._memoryItems;
	}

	private _setMemoryItems(items: MemoryItem[]): void {
		this._memoryItems = items;
		this.sessionManager.appendSessionMemory(items);
		this._emit({ type: "memory_update", items: this._memoryItems });
	}

	setMemoryItem(key: string, value: string): MemoryItem[] {
		const next = upsertMemoryItems(this._memoryItems, key, value, new Date().toISOString());
		this._setMemoryItems(next);
		return next;
	}

	clearMemory(): MemoryItem[] {
		const next = clearMemoryItems();
		this._setMemoryItems(next);
		return next;
	}

	deleteMemoryItem(key: string): MemoryItem[] {
		const next = deleteMemoryItem(this._memoryItems, key);
		this._setMemoryItems(next);
		return next;
	}

	getMemoryItem(key: string): MemoryItem | undefined {
		return getMemoryItem(this._memoryItems, key);
	}

	/**
	 * Get the current task list.
	 */
	getTasks(): ReadonlyArray<Task> {
		return this._tasks;
	}

	/**
	 * Internal: Set tasks, persist, and emit update event.
	 */
	private _setTasks(tasks: Task[]): void {
		this._tasks = tasks;
		this.sessionManager.appendSessionTasks(tasks);
		this._emit({ type: "task_update", tasks: this._tasks });
	}

	/**
	 * Get memory history as a timeline of snapshots.
	 * Delegates to SessionManager.getMemoryHistory() which walks the current branch.
	 *
	 * History is derived from persisted session entries with customType === "session_memory".
	 * Because persistence is snapshot-based, each snapshot contains the complete memory
	 * state at that point, not individual add/update/delete events.
	 */
	getMemoryHistory(): readonly MemoryHistorySnapshot[] {
		return this.sessionManager.getMemoryHistory();
	}

	/**
	 * Get the structured memory history payload used by RPC history consumers.
	 *
	 * This stays current-branch-only and snapshot-based because it delegates to the
	 * same persisted snapshot backend as getMemoryHistory().
	 */
	getStructuredMemoryHistory(): StructuredMemoryHistoryData {
		return buildStructuredMemoryHistoryData(this);
	}

	/**
	 * Compare memory snapshots using the shared structured contract used by RPC.
	 *
	 * - Omit selectors for adjacent latest-vs-previous comparison.
	 * - Provide both baseline and target selectors for explicit comparison.
	 * - Throws if only one selector is provided, matching the RPC command contract.
	 */
	compareMemorySnapshots(options?: { baseline?: string; target?: string }): StructuredMemoryCompareData {
		const hasBaseline = options?.baseline !== undefined;
		const hasTarget = options?.target !== undefined;
		if (hasBaseline !== hasTarget) {
			throw new Error("Provide both baseline and target selectors, or neither for adjacent comparison.");
		}
		return buildStructuredMemoryCompareData(
			this,
			hasBaseline ? { baseline: options!.baseline!, target: options!.target! } : undefined,
		);
	}

	/**
	 * Resolve a memory snapshot by entry ID within the current branch history.
	 * Delegates to SessionManager.findMemorySnapshotById().
	 *
	 * @param id - The entry ID of the snapshot to find
	 * @returns The snapshot with that ID, or undefined if not found in current branch
	 */
	findMemorySnapshotById(id: string): MemoryHistorySnapshot | undefined {
		return this.sessionManager.findMemorySnapshotById(id);
	}

	/**
	 * Strictly resolve a snapshot selector string against current-branch history.
	 * Delegates to SessionManager.resolveMemorySnapshotSelector().
	 *
	 * Resolution order:
	 * 1. Exact full entryId match (always wins)
	 * 2. Exact short-ID match (first 8 chars of an entryId)
	 * 3. Strict unique prefix match — prefix must resolve to exactly one snapshot
	 *
	 * Supports optional surrounding brackets copied from /memory history output: [abcd1234]
	 *
	 * @param input - Raw selector string (may include surrounding brackets)
	 * @returns Resolution result with snapshot or error details
	 */
	resolveMemorySnapshotSelector(input: string): MemorySnapshotResolution {
		return this.sessionManager.resolveMemorySnapshotSelector(input);
	}

	/**
	 * Get the integrated working context surface.
	 *
	 * Returns a real-time snapshot of three state sources:
	 * - memory: persisted session memory (current-branch snapshot-based)
	 * - todo: persisted todo/plan state (current-branch snapshot-based)
	 * - delegatedWork: live current-process delegated subagent state (NOT persisted)
	 *
	 * This reuses the same structured payload exposed via RPC `get_working_context`
	 * and displayed in the interactive working-context panel.
	 *
	 * Honesty contract:
	 * - `memory.isPersisted: true` and `todo.isPersisted: true` — snapshot-backed session state
	 * - `delegatedWork.isPersisted: false` — live runtime state only, resets on session switch/resume
	 *
	 * @returns The current working context snapshot
	 */
	getWorkingContext(): WorkingContext {
		return buildWorkingContext({
			memoryItems: this.getMemoryItems(),
			todos: this.getTodos(),
			tasks: this.getTasks(),
			delegatedWorkSummary: this.getDelegatedWorkSummary(),
		});
	}

	getLatestUltraplanPlan(): UltraplanArtifact | undefined {
		const branch = this.sessionManager.getBranch();
		for (let i = branch.length - 1; i >= 0; i--) {
			const entry = branch[i];
			if (entry.type === "custom" && entry.customType === SESSION_ULTRAPLAN_CUSTOM_TYPE) {
				return parseUltraplanArtifactData(entry.data);
			}
		}
		return undefined;
	}

	private _createUltraplanExecutionContext(): ExtensionContext {
		return (
			this._extensionRunner?.createContext() ?? {
				ui: NO_OP_EXTENSION_UI_CONTEXT,
				hasUI: false,
				cwd: this._cwd,
				sessionManager: this.sessionManager,
				modelRegistry: this._modelRegistry,
				model: this.model,
				isIdle: () => !this.isStreaming,
				abort: () => {
					void this.abort();
				},
				hasPendingMessages: () => this.pendingMessageCount > 0,
				shutdown: () => {
					this._extensionShutdownHandler?.();
				},
				getContextUsage: () => this.getContextUsage(),
				compact: (options?: {
					customInstructions?: string;
					onComplete?: (result: CompactionResult) => void;
					onError?: (error: Error) => void;
				}) => {
					void (async () => {
						try {
							const result = await this.compact(options?.customInstructions);
							options?.onComplete?.(result);
						} catch (error) {
							const err = error instanceof Error ? error : new Error(String(error));
							options?.onError?.(err);
						}
					})();
				},
				getSystemPrompt: () => this.systemPrompt,
				getActiveMissionId: () => this.missionId,
			}
		);
	}

	async runUltraplan(objective: string): Promise<UltraplanRunResult> {
		const trimmedObjective = objective.trim();
		if (trimmedObjective.length === 0) {
			throw new Error("Ultraplan requires a non-empty objective.");
		}

		const subagentTool = [
			...(this._extensionRunner?.getAllRegisteredTools().map((tool) => tool.definition) ?? []),
			...this._customTools,
		].find((tool) => tool.name === "subagent") as UltraplanSubagentTool | undefined;
		if (!subagentTool) {
			throw new Error("Ultraplan requires the local subagent tool to be available.");
		}

		const result = await subagentTool.execute(
			`ultraplan_${Date.now()}`,
			{
				agent: "planner",
				task: buildUltraplanPlannerTask({
					objective: trimmedObjective,
					memoryItems: this.getMemoryItems(),
					todos: this.getTodos(),
				}),
				agentScope: "user",
			},
			undefined,
			undefined,
			this._createUltraplanExecutionContext(),
		);
		if (result.isError) {
			const errorText = result.content.find((entry) => entry.type === "text")?.text ?? "Ultraplan failed.";
			throw new Error(errorText);
		}

		const plannerOutput = result.content.find((entry) => entry.type === "text")?.text?.trim();
		if (!plannerOutput) {
			throw new Error("Planner returned no Ultraplan output.");
		}

		const artifact = parseUltraplanArtifactFromText(plannerOutput);
		this.sessionManager.appendCustomEntry(SESSION_ULTRAPLAN_CUSTOM_TYPE, artifact);

		return {
			artifact,
			displayText: `${formatUltraplanArtifact(artifact)}\n\nPersisted as session-owned Ultraplan state on the current branch.`,
			rawPlannerOutput: plannerOutput,
		};
	}

	/**
	 * Revise the latest persisted Ultraplan plan based on a revision instruction.
	 *
	 * Reads the existing plan artifact and passes it to the planner along with
	 * the revision instruction. The revised result is persisted as a new latest
	 * artifact. Apply remains separate and manual.
	 *
	 * @param instruction - Revision instruction to guide the planner
	 * @returns Revised plan artifact and display text
	 * @throws Error if no persisted Ultraplan plan exists or planner tool unavailable
	 */
	async runUltraplanRevise(instruction: string): Promise<UltraplanRunResult> {
		const trimmedInstruction = instruction.trim();
		if (trimmedInstruction.length === 0) {
			throw new Error("Ultraplan revision requires a non-empty instruction.");
		}

		const existingArtifact = this.getLatestUltraplanPlan();
		if (!existingArtifact) {
			throw new Error("No persisted Ultraplan plan found. Run /ultraplan <objective> first.");
		}

		const subagentTool = [
			...(this._extensionRunner?.getAllRegisteredTools().map((tool) => tool.definition) ?? []),
			...this._customTools,
		].find((tool) => tool.name === "subagent") as UltraplanSubagentTool | undefined;
		if (!subagentTool) {
			throw new Error("Ultraplan requires the local subagent tool to be available.");
		}

		const result = await subagentTool.execute(
			`ultraplan_revise_${Date.now()}`,
			{
				agent: "planner",
				task: buildUltraplanRevisionTask({
					instruction: trimmedInstruction,
					existingArtifact,
					memoryItems: this.getMemoryItems(),
					todos: this.getTodos(),
				}),
				agentScope: "user",
			},
			undefined,
			undefined,
			this._createUltraplanExecutionContext(),
		);
		if (result.isError) {
			const errorText = result.content.find((entry) => entry.type === "text")?.text ?? "Ultraplan revision failed.";
			throw new Error(errorText);
		}

		const plannerOutput = result.content.find((entry) => entry.type === "text")?.text?.trim();
		if (!plannerOutput) {
			throw new Error("Planner returned no Ultraplan output.");
		}

		const artifact = parseUltraplanArtifactFromText(plannerOutput);
		this.sessionManager.appendCustomEntry(SESSION_ULTRAPLAN_CUSTOM_TYPE, artifact);

		return {
			artifact,
			displayText:
				`${formatUltraplanArtifact(artifact)}\n\n` +
				"Persisted as a new latest session-owned Ultraplan state on the current branch. " +
				"Prior plan artifacts remain preserved separately. Apply remains manual; no execution has started.",
			rawPlannerOutput: plannerOutput,
		};
	}

	/**
	 * Apply the latest persisted Ultraplan plan to session-owned todo state.
	 *
	 * This materializes plan output into persisted todo state only.
	 * The original Ultraplan artifact remains intact and no execution starts.
	 *
	 * @returns Result with applied todos and clear applied-not-executed messaging
	 * @throws Error if no persisted Ultraplan plan exists
	 */
	applyUltraplan(): {
		applied: TodoItem[];
		displayText: string;
	} {
		const artifact = this.getLatestUltraplanPlan();
		if (!artifact) {
			throw new Error("No persisted Ultraplan plan found. Run /ultraplan <objective> first.");
		}

		const selection = selectUltraplanApplySteps(artifact);
		if (selection.steps.length === 0) {
			return {
				applied: [],
				displayText:
					"Ultraplan plan found, but it contains no applicable actionable steps.\n\n" +
					"The plan artifact is preserved separately. Apply did not start execution.",
			};
		}

		const existingTodoContents = new Set(this._todos.map((todo) => todo.content));
		const newTodos: TodoItem[] = selection.steps
			.filter((step) => !existingTodoContents.has(step))
			.map((step) => ({
				id: crypto.randomUUID(),
				content: step,
				activeForm: step,
				status: "pending" as const,
			}));

		if (newTodos.length === 0) {
			return {
				applied: [],
				displayText:
					"Ultraplan plan is already represented in session todo state.\n\n" +
					"The plan artifact remains preserved separately. Apply did not start execution.",
			};
		}

		const mergedTodos = [...this._todos, ...newTodos];
		this._setTodos(mergedTodos);

		const sourceLabel =
			selection.source === "actionable_next_steps"
				? "actionable next steps"
				: selection.source === "first_phase_steps"
					? "first phase steps"
					: "plan steps";

		return {
			applied: newTodos,
			displayText:
				`Applied ${newTodos.length} Ultraplan step(s) from the latest persisted plan into session todo state.\n` +
				`Source: ${sourceLabel}. Todo total: ${mergedTodos.length}.\n\n` +
				"The Ultraplan artifact remains preserved separately as plan state. Apply did not start execution.",
		};
	}

	/** Whether compaction or branch summarization is currently running */
	get isCompacting(): boolean {
		return (
			this._autoCompactionAbortController !== undefined ||
			this._compactionAbortController !== undefined ||
			this._branchSummaryAbortController !== undefined
		);
	}

	/** All messages including custom types like BashExecutionMessage */
	get messages(): AgentMessage[] {
		return this.agent.state.messages;
	}

	/** Current steering mode */
	get steeringMode(): "all" | "one-at-a-time" {
		return this.agent.getSteeringMode();
	}

	/** Current follow-up mode */
	get followUpMode(): "all" | "one-at-a-time" {
		return this.agent.getFollowUpMode();
	}

	/** Current session file path, or undefined if sessions are disabled */
	get sessionFile(): string | undefined {
		return this.sessionManager.getSessionFile();
	}

	/** Current session ID */
	get sessionId(): string {
		return this.sessionManager.getSessionId();
	}

	/** Current session display name, if set */
	get sessionName(): string | undefined {
		return this.sessionManager.getSessionName();
	}

	/** Scoped models for cycling (from --models flag) */
	get scopedModels(): ReadonlyArray<{ model: Model<any>; thinkingLevel?: ThinkingLevel }> {
		return this._scopedModels;
	}

	/** Update scoped models for cycling */
	setScopedModels(scopedModels: Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }>): void {
		this._scopedModels = scopedModels;
	}

	/** Enable or disable the brief-only output contract for this session runtime. */
	setBriefOnly(enabled: boolean): void {
		if (this._briefOnly === enabled) {
			return;
		}

		this._briefOnly = enabled;
		this._baseSystemPrompt = this._rebuildSystemPrompt(this.getActiveToolNames());
		this._applyBaseSystemPrompt();
	}

	/** Set the Caveman output compression level for this session runtime. */
	setCavemanLevel(level: CavemanLevel): void {
		if (this._cavemanLevel === level) {
			return;
		}

		this._cavemanLevel = level;
		this._baseSystemPrompt = this._rebuildSystemPrompt(this.getActiveToolNames());
		this._applyBaseSystemPrompt();
	}

	queueByTheWay(note: string): void {
		this._pendingNextTurnMessages.push({
			role: "custom",
			...createBtwNextTurnMessage(note),
			timestamp: Date.now(),
		});
	}

	/** File-based prompt templates */
	get promptTemplates(): ReadonlyArray<PromptTemplate> {
		return this._resourceLoader.getPrompts().prompts;
	}

	private _normalizePromptSnippet(text: string | undefined): string | undefined {
		if (!text) return undefined;
		const oneLine = text
			.replace(/[\r\n]+/g, " ")
			.replace(/\s+/g, " ")
			.trim();
		return oneLine.length > 0 ? oneLine : undefined;
	}

	private _normalizePromptGuidelines(guidelines: string[] | undefined): string[] {
		if (!guidelines || guidelines.length === 0) {
			return [];
		}

		const unique = new Set<string>();
		for (const guideline of guidelines) {
			const normalized = guideline.trim();
			if (normalized.length > 0) {
				unique.add(normalized);
			}
		}
		return Array.from(unique);
	}

	private _augmentCompactionSummary(summary: string): string {
		const memorySection =
			this._memoryItems.length > 0
				? `\n\n## Active Session Memory\n${this._memoryItems.map((item) => `- ${item.key}: ${item.value}`).join("\n")}`
				: "";
		const todoSection =
			this._todos.length > 0
				? `\n\n## Active Todo State\n${this._todos.map((todo) => `- [${todo.status}] ${todo.status === "in_progress" ? todo.activeForm : todo.content}`).join("\n")}`
				: "";
		return `${summary}${memorySection}${todoSection}`;
	}

	private async _finalizeCompaction(options: {
		summary: string;
		firstKeptEntryId: string;
		tokensBefore: number;
		details: unknown;
		fromExtension: boolean;
	}): Promise<{ result: CompactionResult; savedCompactionEntry: CompactionEntry | undefined }> {
		const { summary, firstKeptEntryId, tokensBefore, details, fromExtension } = options;
		const persistedSummary = this._augmentCompactionSummary(summary);

		this.sessionManager.appendCompaction(persistedSummary, firstKeptEntryId, tokensBefore, details, fromExtension);
		const newEntries = this.sessionManager.getEntries();
		const sessionContext = this.sessionManager.buildSessionContext();
		this.agent.replaceMessages(sessionContext.messages);

		const savedCompactionEntry = newEntries.find((e) => e.type === "compaction" && e.summary === persistedSummary) as
			| CompactionEntry
			| undefined;

		if (this._extensionRunner && savedCompactionEntry) {
			await this._extensionRunner.emit({
				type: "session_compact",
				compactionEntry: savedCompactionEntry,
				fromExtension,
			});
		}

		return {
			result: {
				summary: persistedSummary,
				firstKeptEntryId,
				tokensBefore,
				details,
			},
			savedCompactionEntry,
		};
	}

	private _getBriefOnlyPromptGuidelines(): string[] {
		if (!this._briefOnly) {
			return [];
		}

		return [
			"Brief-only mode is enabled for this session.",
			"Suppress ordinary assistant prose.",
			"Do not emit assistant narration, summaries, or conversational filler unless it is necessary for honest error reporting or operational transparency.",
			"Do not hide tool activity, approvals, warnings, errors, or other security-relevant execution details.",
			"User-visible brief updates may be delivered separately by the runtime; avoid duplicating them in assistant prose.",
		];
	}

	private _getCavemanPromptGuidelines(): string[] {
		if (this._cavemanLevel === "off") {
			return [];
		}

		const safetyExclusions = [
			"CRITICAL SAFETY - NEVER compress, omit, modify, or summarize the following under any circumstances:",
			"  - Tool calls, tool arguments, tool results, and command output",
			"  - JSON, YAML, XML, frontmatter, schemas, or any machine-parsed envelopes",
			"  - details.results and all result fields used for parent validation",
			"  - Task, planner, slice, handoff, run, and verification state",
			"  - Code, file paths, URLs, commit hashes, CLI commands, and exact error messages",
			"  - Security confirmations, destructive action confirmations, and ordered procedures",
			"  - Delegated evidence from reviewer, security, pentester, and librarian agents",
			"These global native rules cannot be weakened by project-local configuration or skills.",
			"Delegated narrative summaries are acceptable only for prose, never for structured results.",
			"Preserve the user's original language in all modes. Do not change language, terminology, or phrasing used by the user.",
		];

		if (this._cavemanLevel === "lite") {
			return [
				"Caveman Lite output compression is active for this session. Use professional complete sentences that remove filler words, hedging language, and unnecessary elaboration. Present each step clearly and concisely while maintaining natural prose flow.",
				...safetyExclusions,
			];
		}

		if (this._cavemanLevel === "full") {
			return [
				"Caveman Full output compression is active for this session. Use tighter phrasing. Favor sentence fragments only where meaning remains unambiguous. Eliminate redundant words and softening phrases.",
				...safetyExclusions,
			];
		}

		// "ultra"
		return [
			"Caveman Ultra output compression is active for this session. Use only simple, non-risky answers with minimal prose. MANDATORY: For warnings, destructive actions/confirmations, ambiguity, ordered or multi-step procedures, or repeated questions, you MUST automatically fall back to clear, complete prose. Never use keyword-level output for these situations.",
			...safetyExclusions,
		];
	}

	private _rebuildSystemPrompt(toolNames: string[]): string {
		const validToolNames = toolNames.filter((name) => this._toolRegistry.has(name));
		const toolSnippets: Record<string, string> = {};
		const promptGuidelines: string[] = [];
		for (const name of validToolNames) {
			const snippet = this._toolPromptSnippets.get(name);
			if (snippet) {
				toolSnippets[name] = snippet;
			}

			const toolGuidelines = this._toolPromptGuidelines.get(name);
			if (toolGuidelines) {
				promptGuidelines.push(...toolGuidelines);
			}
		}
		promptGuidelines.push(...this._getBriefOnlyPromptGuidelines());
		promptGuidelines.push(...this._getCavemanPromptGuidelines());

		const loaderSystemPrompt = this._resourceLoader.getSystemPrompt();
		const loaderAppendSystemPrompt = this._resourceLoader.getAppendSystemPrompt();
		const appendSystemPrompt =
			loaderAppendSystemPrompt.length > 0 ? loaderAppendSystemPrompt.join("\n\n") : undefined;
		const loadedSkills = this._resourceLoader.getSkills().skills;
		const loadedContextFiles = this._resourceLoader.getAgentsFiles().agentsFiles;

		const regions = buildSystemPromptRegions({
			cwd: this._cwd,
			skills: loadedSkills,
			contextFiles: loadedContextFiles,
			customPrompt: loaderSystemPrompt,
			appendSystemPrompt,
			selectedTools: validToolNames,
			toolSnippets,
			promptGuidelines,
		});
		this._baseDynamicPrompt = regions.dynamicSuffix;
		return regions.stablePrefix;
	}

	private _applyBaseSystemPrompt(): void {
		this.agent.setSystemPrompt(this._baseSystemPrompt);
		this.agent.setDynamicPrompt(this._baseDynamicPrompt);
	}

	private _getCompleteBaseSystemPrompt(): string {
		return this._baseDynamicPrompt
			? `${this._baseSystemPrompt}\n\n${this._baseDynamicPrompt}`
			: this._baseSystemPrompt;
	}

	// =========================================================================
	// Prompting
	// =========================================================================

	/**
	 * Send a prompt to the agent.
	 * - Handles extension commands (registered via pi.registerCommand) immediately, even during streaming
	 * - Expands file-based prompt templates by default
	 * - During streaming, queues via steer() or followUp() based on streamingBehavior option
	 * - Validates model and API key before sending (when not streaming)
	 * @throws Error if streaming and no streamingBehavior specified
	 * @throws Error if no model selected or no API key available (when not streaming)
	 */
	async prompt(text: string, options?: PromptOptions): Promise<void> {
		const expandPromptTemplates = options?.expandPromptTemplates ?? true;

		// Handle extension commands first (execute immediately, even during streaming)
		// Extension commands manage their own LLM interaction via pi.sendMessage()
		if (expandPromptTemplates && text.startsWith("/")) {
			const handled = await this._tryExecuteExtensionCommand(text);
			if (handled) {
				// Extension command executed, no prompt to send
				return;
			}
		}

		// Emit input event for extension interception (before skill/template expansion)
		let currentText = text;
		let currentImages = options?.images;
		if (this._extensionRunner?.hasHandlers("input")) {
			const inputResult = await this._extensionRunner.emitInput(
				currentText,
				currentImages,
				options?.source ?? "interactive",
			);
			if (inputResult.action === "handled") {
				return;
			}
			if (inputResult.action === "transform") {
				currentText = inputResult.text;
				currentImages = inputResult.images ?? currentImages;
			}
		}

		// Expand skill commands (/skill:name args) and prompt templates (/template args)
		let expandedText = currentText;
		if (expandPromptTemplates) {
			expandedText = this._expandSkillCommand(expandedText);
			expandedText = expandPromptTemplate(expandedText, [...this.promptTemplates]);
		}

		// If streaming, queue via steer() or followUp() based on option
		if (this.isStreaming) {
			if (!options?.streamingBehavior) {
				throw new Error(
					"Agent is already processing. Specify streamingBehavior ('steer' or 'followUp') to queue the message.",
				);
			}
			if (options.streamingBehavior === "followUp") {
				await this._queueFollowUp(expandedText, currentImages);
			} else {
				await this._queueSteer(expandedText, currentImages);
			}
			return;
		}

		// Flush any pending bash messages before the new prompt
		this._flushPendingBashMessages();

		// Validate model
		if (!this.model) {
			throw new Error(
				"No model selected.\n\n" +
					`Use /login or set an API key environment variable. See ${join(getDocsPath(), "providers.md")}\n\n` +
					"Then use /model to select a model.",
			);
		}

		// Validate API key
		let apiKey = await this._modelRegistry.getApiKey(this.model);
		if (!apiKey) {
			let fallbackSuccess = false;
			while (!apiKey && this._fallbackIndex < this._fallbackModels.length - 1) {
				this._fallbackIndex++;
				const fallbackModel = this._fallbackModels[this._fallbackIndex];
				apiKey = await this._modelRegistry.getApiKey(fallbackModel);
				if (apiKey) {
					this._emit({
						type: "auto_fallback",
						model: this.model,
						fallbackModel,
						reason: "Missing API key for primary model",
					});
					await this.setModel(fallbackModel);
					fallbackSuccess = true;
					break;
				}
			}

			if (!fallbackSuccess && !apiKey) {
				const isOAuth = this._modelRegistry.isUsingOAuth(this.model);
				if (isOAuth) {
					throw new Error(
						`Authentication failed for "${this.model.provider}". ` +
							`Credentials may have expired or network is unavailable. ` +
							`Run '/login ${this.model.provider}' to re-authenticate.`,
					);
				}
				throw new Error(
					`No API key found for ${this.model.provider}.\n\n` +
						`Use /login or set an API key environment variable. See ${join(getDocsPath(), "providers.md")}`,
				);
			}
		}

		// Check if we need to compact before sending (catches aborted responses)
		const lastAssistant = this._findLastAssistantMessage();
		if (lastAssistant) {
			await this._checkCompaction(lastAssistant, false);
		}

		// Build messages array (custom message if any, then user message)
		const messages: AgentMessage[] = [];

		// Add user message
		const userContent: (TextContent | ImageContent)[] = [{ type: "text", text: expandedText }];
		if (currentImages) {
			userContent.push(...currentImages);
		}
		messages.push({
			role: "user",
			content: userContent,
			timestamp: Date.now(),
		});

		// Inject any pending "nextTurn" messages as context alongside the user message
		for (const msg of this._pendingNextTurnMessages) {
			messages.push(msg);
		}
		this._pendingNextTurnMessages = [];

		// Emit before_agent_start extension event
		if (this._extensionRunner) {
			const result = await this._extensionRunner.emitBeforeAgentStart(
				expandedText,
				currentImages,
				this._getCompleteBaseSystemPrompt(),
			);
			// Add all custom messages from extensions
			if (result?.messages) {
				for (const msg of result.messages) {
					messages.push({
						role: "custom",
						customType: msg.customType,
						content: msg.content,
						display: msg.display,
						details: msg.details,
						timestamp: Date.now(),
					});
				}
			}
			// Apply extension-modified system prompt, or reset to base
			if (result?.systemPrompt && result.systemPrompt !== this._getCompleteBaseSystemPrompt()) {
				this.agent.setSystemPrompt(result.systemPrompt);
				this.agent.setDynamicPrompt(undefined);
			} else {
				// Ensure we're using the base prompt (in case previous turn had modifications)
				this._applyBaseSystemPrompt();
			}
		}

		this._todoLoopGuard.resetOnNewUserMessage();
		this._todoUpdateRejectionState.count = 0;
		this._todoPerTurnLock.currentTurn++;
		this._todoPerTurnLock.lockedUntil = -1;
		this._todoPerTurnLock.reservedUntil = -1;
		if (this._toolRegistry.has("todo_write") && !this.getActiveToolNames().includes("todo_write")) {
			this.setActiveToolsByName([...this.getActiveToolNames(), "todo_write"]);
		}

		// Reliability Kernel: reset per-run finalization budget and inject the
		// authoritative mission state into this turn's context.
		this._reliability?.resetRun();
		this._applyReliabilityContext();

		await this.agent.prompt(messages);
		await this.waitForRetry();
	}

	/**
	 * Try to execute an extension command. Returns true if command was found and executed.
	 */
	private async _tryExecuteExtensionCommand(text: string): Promise<boolean> {
		if (!this._extensionRunner) return false;

		// Parse command name and args
		const spaceIndex = text.indexOf(" ");
		const commandName = spaceIndex === -1 ? text.slice(1) : text.slice(1, spaceIndex);
		const args = spaceIndex === -1 ? "" : text.slice(spaceIndex + 1);

		const command = this._extensionRunner.getCommand(commandName);
		if (!command) return false;

		// Get command context from extension runner (includes session control methods)
		const ctx = this._extensionRunner.createCommandContext();

		try {
			await command.handler(args, ctx);
			return true;
		} catch (err) {
			// Emit error via extension runner
			this._extensionRunner.emitError({
				extensionPath: `command:${commandName}`,
				event: "command",
				error: err instanceof Error ? err.message : String(err),
			});
			return true;
		}
	}

	/**
	 * Expand skill commands (/skill:name args) to their full content.
	 * Returns the expanded text, or the original text if not a skill command or skill not found.
	 * Emits errors via extension runner if file read fails.
	 */
	private _expandSkillCommand(text: string): string {
		if (!text.startsWith("/skill:")) return text;

		const spaceIndex = text.indexOf(" ");
		const skillName = spaceIndex === -1 ? text.slice(7) : text.slice(7, spaceIndex);
		const args = spaceIndex === -1 ? "" : text.slice(spaceIndex + 1).trim();

		const skill = this.resourceLoader.getSkills().skills.find((s) => s.name === skillName);
		if (!skill) return text; // Unknown skill, pass through

		try {
			const content = readFileSync(skill.filePath, "utf-8");
			const body = stripFrontmatter(content).trim();
			const skillBlock = `<skill name="${skill.name}" location="${skill.filePath}">\nReferences are relative to ${skill.baseDir}.\n\n${body}\n</skill>`;
			return args ? `${skillBlock}\n\n${args}` : skillBlock;
		} catch (err) {
			// Emit error like extension commands do
			this._extensionRunner?.emitError({
				extensionPath: skill.filePath,
				event: "skill_expansion",
				error: err instanceof Error ? err.message : String(err),
			});
			return text; // Return original on error
		}
	}

	/**
	 * Queue a steering message while the agent is running.
	 * Delivered after the current assistant turn finishes executing its tool calls,
	 * before the next LLM call.
	 * Expands skill commands and prompt templates. Errors on extension commands.
	 * @param images Optional image attachments to include with the message
	 * @throws Error if text is an extension command
	 */
	async steer(text: string, images?: ImageContent[]): Promise<void> {
		// Check for extension commands (cannot be queued)
		if (text.startsWith("/")) {
			this._throwIfExtensionCommand(text);
		}

		// Expand skill commands and prompt templates
		let expandedText = this._expandSkillCommand(text);
		expandedText = expandPromptTemplate(expandedText, [...this.promptTemplates]);

		await this._queueSteer(expandedText, images);
	}

	/**
	 * Queue a follow-up message to be processed after the agent finishes.
	 * Delivered only when agent has no more tool calls or steering messages.
	 * Expands skill commands and prompt templates. Errors on extension commands.
	 * @param images Optional image attachments to include with the message
	 * @throws Error if text is an extension command
	 */
	async followUp(text: string, images?: ImageContent[]): Promise<void> {
		// Check for extension commands (cannot be queued)
		if (text.startsWith("/")) {
			this._throwIfExtensionCommand(text);
		}

		// Expand skill commands and prompt templates
		let expandedText = this._expandSkillCommand(text);
		expandedText = expandPromptTemplate(expandedText, [...this.promptTemplates]);

		await this._queueFollowUp(expandedText, images);
	}

	/**
	 * Internal: Queue a steering message (already expanded, no extension command check).
	 */
	private async _queueSteer(text: string, images?: ImageContent[]): Promise<void> {
		this._steeringMessages.push(text);
		const content: (TextContent | ImageContent)[] = [{ type: "text", text }];
		if (images) {
			content.push(...images);
		}
		this.agent.steer({
			role: "user",
			content,
			timestamp: Date.now(),
		});
	}

	/**
	 * Internal: Queue a follow-up message (already expanded, no extension command check).
	 */
	private async _queueFollowUp(text: string, images?: ImageContent[]): Promise<void> {
		this._followUpMessages.push(text);
		const content: (TextContent | ImageContent)[] = [{ type: "text", text }];
		if (images) {
			content.push(...images);
		}
		this.agent.followUp({
			role: "user",
			content,
			timestamp: Date.now(),
		});
	}

	/**
	 * Throw an error if the text is an extension command.
	 */
	private _throwIfExtensionCommand(text: string): void {
		if (!this._extensionRunner) return;

		const spaceIndex = text.indexOf(" ");
		const commandName = spaceIndex === -1 ? text.slice(1) : text.slice(1, spaceIndex);
		const command = this._extensionRunner.getCommand(commandName);

		if (command) {
			throw new Error(
				`Extension command "/${commandName}" cannot be queued. Use prompt() or execute the command when not streaming.`,
			);
		}
	}

	/**
	 * Send a custom message to the session. Creates a CustomMessageEntry.
	 *
	 * Handles three cases:
	 * - Streaming: queues message, processed when loop pulls from queue
	 * - Not streaming + triggerTurn: appends to state/session, starts new turn
	 * - Not streaming + no trigger: appends to state/session, no turn
	 *
	 * @param message Custom message with customType, content, display, details
	 * @param options.triggerTurn If true and not streaming, triggers a new LLM turn
	 * @param options.deliverAs Delivery mode: "steer", "followUp", or "nextTurn"
	 */
	async sendCustomMessage<T = unknown>(
		message: Pick<CustomMessage<T>, "customType" | "content" | "display" | "details">,
		options?: { triggerTurn?: boolean; deliverAs?: "steer" | "followUp" | "nextTurn" },
	): Promise<void> {
		const appMessage = {
			role: "custom" as const,
			customType: message.customType,
			content: message.content,
			display: message.display,
			details: message.details,
			timestamp: Date.now(),
		} satisfies CustomMessage<T>;
		if (options?.deliverAs === "nextTurn") {
			this._pendingNextTurnMessages.push(appMessage);
		} else if (this.isStreaming) {
			if (options?.deliverAs === "followUp") {
				this.agent.followUp(appMessage);
			} else {
				this.agent.steer(appMessage);
			}
		} else if (options?.triggerTurn) {
			await this.agent.prompt(appMessage);
		} else {
			this.agent.appendMessage(appMessage);
			this.sessionManager.appendCustomMessageEntry(
				message.customType,
				message.content,
				message.display,
				message.details,
			);
			this._emit({ type: "message_start", message: appMessage });
			this._emit({ type: "message_end", message: appMessage });
		}
	}

	/**
	 * Send a user message to the agent. Always triggers a turn.
	 * When the agent is streaming, use deliverAs to specify how to queue the message.
	 *
	 * @param content User message content (string or content array)
	 * @param options.deliverAs Delivery mode when streaming: "steer" or "followUp"
	 */
	async sendUserMessage(
		content: string | (TextContent | ImageContent)[],
		options?: { deliverAs?: "steer" | "followUp" },
	): Promise<void> {
		// Normalize content to text string + optional images
		let text: string;
		let images: ImageContent[] | undefined;

		if (typeof content === "string") {
			text = content;
		} else {
			const textParts: string[] = [];
			images = [];
			for (const part of content) {
				if (part.type === "text") {
					textParts.push(part.text);
				} else {
					images.push(part);
				}
			}
			text = textParts.join("\n");
			if (images.length === 0) images = undefined;
		}

		// Use prompt() with expandPromptTemplates: false to skip command handling and template expansion
		await this.prompt(text, {
			expandPromptTemplates: false,
			streamingBehavior: options?.deliverAs,
			images,
			source: "extension",
		});
	}

	/**
	 * Clear all queued messages and return them.
	 * Useful for restoring to editor when user aborts.
	 * @returns Object with steering and followUp arrays
	 */
	clearQueue(): { steering: string[]; followUp: string[] } {
		const steering = [...this._steeringMessages];
		const followUp = [...this._followUpMessages];
		this._steeringMessages = [];
		this._followUpMessages = [];
		this.agent.clearAllQueues();
		return { steering, followUp };
	}

	/** Number of pending messages (includes both steering and follow-up) */
	get pendingMessageCount(): number {
		return this._steeringMessages.length + this._followUpMessages.length;
	}

	/** Get pending steering messages (read-only) */
	getSteeringMessages(): readonly string[] {
		return this._steeringMessages;
	}

	/** Get pending follow-up messages (read-only) */
	getFollowUpMessages(): readonly string[] {
		return this._followUpMessages;
	}

	get resourceLoader(): ResourceLoader {
		return this._resourceLoader;
	}

	/**
	 * Abort current operation and wait for agent to become idle.
	 */
	async abort(): Promise<void> {
		this.abortRetry();
		this.agent.abort();
		await this.agent.waitForIdle();
	}

	/**
	 * Start a new session, optionally with initial messages and parent tracking.
	 * Clears all messages and starts a new session.
	 * Listeners are preserved and will continue receiving events.
	 * @param options.parentSession - Optional parent session path for tracking
	 * @param options.setup - Optional callback to initialize session (e.g., append messages)
	 * @returns true if completed, false if cancelled by extension
	 */
	async newSession(options?: {
		parentSession?: string;
		setup?: (sessionManager: SessionManager) => Promise<void>;
	}): Promise<boolean> {
		const previousSessionFile = this.sessionFile;

		// Emit session_before_switch event with reason "new" (can be cancelled)
		if (this._extensionRunner?.hasHandlers("session_before_switch")) {
			const result = (await this._extensionRunner.emit({
				type: "session_before_switch",
				reason: "new",
			})) as SessionBeforeSwitchResult | undefined;

			if (result?.cancel) {
				return false;
			}
		}

		this._disconnectFromAgent();
		await this.abort();
		this.agent.reset();
		this.sessionManager.newSession({ parentSession: options?.parentSession });
		this.agent.sessionId = this.sessionManager.getSessionId();
		this._steeringMessages = [];
		this._followUpMessages = [];
		this._pendingNextTurnMessages = [];
		this._todos = [];
		this._todoRevision = 0;
		this._resetTodoEngine();
		this._todoReadSnapshot = null;
		this._todoUpdateRejectionState.count = 0;
		this._todoPerTurnLock.currentTurn = 0;
		this._todoPerTurnLock.lockedUntil = -1;
		this._todoPerTurnLock.reservedUntil = -1;
		this._memoryItems = [];
		this._delegatedTasks = [];
		this._tasks = [];
		this._cavemanLevel = "off";
		this._briefOnly = false;

		this._baseSystemPrompt = this._rebuildSystemPrompt(this.getActiveToolNames());
		this._applyBaseSystemPrompt();

		this.sessionManager.appendThinkingLevelChange(this.thinkingLevel);
		this._emit({ type: "todo_update", todos: this._todos });
		this._emit({ type: "memory_update", items: this._memoryItems });

		// Run setup callback if provided (e.g., to append initial messages)
		if (options?.setup) {
			await options.setup(this.sessionManager);
			// Sync agent state with session manager after setup
			const sessionContext = this.sessionManager.buildSessionContext();
			this.agent.replaceMessages(sessionContext.messages);
		}

		this._reconnectToAgent();

		// Emit session_switch event with reason "new" to extensions
		if (this._extensionRunner) {
			await this._extensionRunner.emit({
				type: "session_switch",
				reason: "new",
				previousSessionFile,
			});
		}

		// Emit session event to custom tools
		return true;
	}

	// =========================================================================
	// Model Management
	// =========================================================================

	private async _emitModelSelect(
		nextModel: Model<any>,
		previousModel: Model<any> | undefined,
		source: "set" | "cycle" | "restore",
	): Promise<void> {
		if (!this._extensionRunner) return;
		if (modelsAreEqual(previousModel, nextModel)) return;
		await this._extensionRunner.emit({
			type: "model_select",
			model: nextModel,
			previousModel,
			source,
		});
	}

	/**
	 * Set model directly.
	 * Validates API key, saves to session and settings.
	 * @throws Error if no API key available for the model
	 */
	async setModel(model: Model<any>): Promise<void> {
		const apiKey = await this._modelRegistry.getApiKey(model);
		if (!apiKey) {
			throw new Error(`No API key for ${model.provider}/${model.id}`);
		}

		const previousModel = this.model;
		const thinkingLevel = this._getThinkingLevelForModelSwitch();
		this.agent.setModel(model);
		this.sessionManager.appendModelChange(model.provider, model.id);
		this.settingsManager.setDefaultModelAndProvider(model.provider, model.id);

		// Re-clamp thinking level for new model's capabilities
		this.setThinkingLevel(thinkingLevel);

		await this._emitModelSelect(model, previousModel, "set");
	}

	/**
	 * Cycle to next/previous model.
	 * Uses scoped models (from --models flag) if available, otherwise all available models.
	 * @param direction - "forward" (default) or "backward"
	 * @returns The new model info, or undefined if only one model available
	 */
	async cycleModel(direction: "forward" | "backward" = "forward"): Promise<ModelCycleResult | undefined> {
		if (this._scopedModels.length > 0) {
			return this._cycleScopedModel(direction);
		}
		return this._cycleAvailableModel(direction);
	}

	private async _getScopedModelsWithApiKey(): Promise<Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }>> {
		const apiKeysByProvider = new Map<string, string | undefined>();
		const result: Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }> = [];

		for (const scoped of this._scopedModels) {
			const provider = scoped.model.provider;
			let apiKey: string | undefined;
			if (apiKeysByProvider.has(provider)) {
				apiKey = apiKeysByProvider.get(provider);
			} else {
				apiKey = await this._modelRegistry.getApiKeyForProvider(provider);
				apiKeysByProvider.set(provider, apiKey);
			}

			if (apiKey) {
				result.push(scoped);
			}
		}

		return result;
	}

	private async _cycleScopedModel(direction: "forward" | "backward"): Promise<ModelCycleResult | undefined> {
		const scopedModels = await this._getScopedModelsWithApiKey();
		if (scopedModels.length <= 1) return undefined;

		const currentModel = this.model;
		let currentIndex = scopedModels.findIndex((sm) => modelsAreEqual(sm.model, currentModel));

		if (currentIndex === -1) currentIndex = 0;
		const len = scopedModels.length;
		const nextIndex = direction === "forward" ? (currentIndex + 1) % len : (currentIndex - 1 + len) % len;
		const next = scopedModels[nextIndex];
		const thinkingLevel = this._getThinkingLevelForModelSwitch(next.thinkingLevel);

		// Apply model
		this.agent.setModel(next.model);
		this.sessionManager.appendModelChange(next.model.provider, next.model.id);
		this.settingsManager.setDefaultModelAndProvider(next.model.provider, next.model.id);

		// Apply thinking level.
		// - Explicit scoped model thinking level overrides current session level
		// - Undefined scoped model thinking level inherits the current session preference
		// setThinkingLevel clamps to model capabilities.
		this.setThinkingLevel(thinkingLevel);

		await this._emitModelSelect(next.model, currentModel, "cycle");

		return { model: next.model, thinkingLevel: this.thinkingLevel, isScoped: true };
	}

	private async _cycleAvailableModel(direction: "forward" | "backward"): Promise<ModelCycleResult | undefined> {
		const availableModels = await this._modelRegistry.getAvailable();
		if (availableModels.length <= 1) return undefined;

		const currentModel = this.model;
		let currentIndex = availableModels.findIndex((m) => modelsAreEqual(m, currentModel));

		if (currentIndex === -1) currentIndex = 0;
		const len = availableModels.length;
		const nextIndex = direction === "forward" ? (currentIndex + 1) % len : (currentIndex - 1 + len) % len;
		const nextModel = availableModels[nextIndex];

		const apiKey = await this._modelRegistry.getApiKey(nextModel);
		if (!apiKey) {
			throw new Error(`No API key for ${nextModel.provider}/${nextModel.id}`);
		}

		const thinkingLevel = this._getThinkingLevelForModelSwitch();
		this.agent.setModel(nextModel);
		this.sessionManager.appendModelChange(nextModel.provider, nextModel.id);
		this.settingsManager.setDefaultModelAndProvider(nextModel.provider, nextModel.id);

		// Re-clamp thinking level for new model's capabilities
		this.setThinkingLevel(thinkingLevel);

		await this._emitModelSelect(nextModel, currentModel, "cycle");

		return { model: nextModel, thinkingLevel: this.thinkingLevel, isScoped: false };
	}

	// =========================================================================
	// Thinking Level Management
	// =========================================================================

	/**
	 * Set thinking level.
	 * Clamps to model capabilities based on available thinking levels.
	 * Saves to session and settings only if the level actually changes.
	 */
	setThinkingLevel(level: ThinkingLevel): void {
		const availableLevels = this.getAvailableThinkingLevels();
		const effectiveLevel = availableLevels.includes(level) ? level : this._clampThinkingLevel(level, availableLevels);

		// Only persist if actually changing
		const isChanging = effectiveLevel !== this.agent.state.thinkingLevel;

		this.agent.setThinkingLevel(effectiveLevel);

		if (isChanging) {
			this.sessionManager.appendThinkingLevelChange(effectiveLevel);
			if (this.supportsThinking() || effectiveLevel !== "off") {
				this.settingsManager.setDefaultThinkingLevel(effectiveLevel);
			}
		}
	}

	/**
	 * Cycle to next thinking level.
	 * @returns New level, or undefined if model doesn't support thinking
	 */
	cycleThinkingLevel(): ThinkingLevel | undefined {
		if (!this.supportsThinking()) return undefined;

		const levels = this.getAvailableThinkingLevels();
		const currentIndex = levels.indexOf(this.thinkingLevel);
		const nextIndex = (currentIndex + 1) % levels.length;
		const nextLevel = levels[nextIndex];

		this.setThinkingLevel(nextLevel);
		return nextLevel;
	}

	/**
	 * Get available thinking levels for current model.
	 * The provider will clamp to what the specific model supports internally.
	 */
	getAvailableThinkingLevels(): ThinkingLevel[] {
		if (!this.supportsThinking()) return ["off"];
		return this.supportsXhighThinking() ? THINKING_LEVELS_WITH_XHIGH : THINKING_LEVELS;
	}

	/**
	 * Check if current model supports xhigh thinking level.
	 */
	supportsXhighThinking(): boolean {
		return this.model ? supportsXhigh(this.model) : false;
	}

	/**
	 * Check if current model supports thinking/reasoning.
	 */
	supportsThinking(): boolean {
		return !!this.model?.reasoning;
	}

	private _getThinkingLevelForModelSwitch(explicitLevel?: ThinkingLevel): ThinkingLevel {
		if (explicitLevel !== undefined) {
			return explicitLevel;
		}
		if (!this.supportsThinking()) {
			return this.settingsManager.getDefaultThinkingLevel() ?? DEFAULT_THINKING_LEVEL;
		}
		return this.thinkingLevel;
	}

	private _clampThinkingLevel(level: ThinkingLevel, availableLevels: ThinkingLevel[]): ThinkingLevel {
		const ordered = THINKING_LEVELS_WITH_XHIGH;
		const available = new Set(availableLevels);
		const requestedIndex = ordered.indexOf(level);
		if (requestedIndex === -1) {
			return availableLevels[0] ?? "off";
		}
		for (let i = requestedIndex; i < ordered.length; i++) {
			const candidate = ordered[i];
			if (available.has(candidate)) return candidate;
		}
		for (let i = requestedIndex - 1; i >= 0; i--) {
			const candidate = ordered[i];
			if (available.has(candidate)) return candidate;
		}
		return availableLevels[0] ?? "off";
	}

	// =========================================================================
	// Queue Mode Management
	// =========================================================================

	/**
	 * Set steering message mode.
	 * Saves to settings.
	 */
	setSteeringMode(mode: "all" | "one-at-a-time"): void {
		this.agent.setSteeringMode(mode);
		this.settingsManager.setSteeringMode(mode);
	}

	/**
	 * Set follow-up message mode.
	 * Saves to settings.
	 */
	setFollowUpMode(mode: "all" | "one-at-a-time"): void {
		this.agent.setFollowUpMode(mode);
		this.settingsManager.setFollowUpMode(mode);
	}

	// =========================================================================
	// Compaction
	// =========================================================================

	/**
	 * Manually compact the session context.
	 * Aborts current agent operation first.
	 * @param customInstructions Optional instructions for the compaction summary
	 */
	async compact(customInstructions?: string): Promise<CompactionResult> {
		this._disconnectFromAgent();
		await this.abort();
		this._compactionAbortController = new AbortController();

		try {
			if (!this.model) {
				throw new Error("No model selected");
			}

			const apiKey = await this._modelRegistry.getApiKey(this.model);
			if (!apiKey) {
				throw new Error(`No API key for ${this.model.provider}`);
			}

			const pathEntries = this.sessionManager.getBranch();
			const settings = this.settingsManager.getCompactionSettings();

			const preparation = prepareCompaction(pathEntries, settings);
			if (!preparation) {
				// Check why we can't compact
				const lastEntry = pathEntries[pathEntries.length - 1];
				if (lastEntry?.type === "compaction") {
					throw new Error("Already compacted");
				}
				throw new Error("Nothing to compact (session too small)");
			}

			let extensionCompaction: CompactionResult | undefined;
			let fromExtension = false;

			if (this._extensionRunner?.hasHandlers("session_before_compact")) {
				const result = (await this._extensionRunner.emit({
					type: "session_before_compact",
					preparation,
					branchEntries: pathEntries,
					customInstructions,
					signal: this._compactionAbortController.signal,
				})) as SessionBeforeCompactResult | undefined;

				if (result?.cancel) {
					throw new Error("Compaction cancelled");
				}

				if (result?.compaction) {
					extensionCompaction = result.compaction;
					fromExtension = true;
				}
			}

			let summary: string;
			let firstKeptEntryId: string;
			let tokensBefore: number;
			let details: unknown;

			if (extensionCompaction) {
				// Extension provided compaction content
				summary = extensionCompaction.summary;
				firstKeptEntryId = extensionCompaction.firstKeptEntryId;
				tokensBefore = extensionCompaction.tokensBefore;
				details = extensionCompaction.details;
			} else {
				// Generate compaction result
				const result = await compact(
					preparation,
					this.model,
					apiKey,
					customInstructions,
					this._compactionAbortController.signal,
				);
				summary = result.summary;
				firstKeptEntryId = result.firstKeptEntryId;
				tokensBefore = result.tokensBefore;
				details = result.details;
			}

			if (this._compactionAbortController.signal.aborted) {
				throw new Error("Compaction cancelled");
			}
			details = createDeterministicCompactionDetails(preparation, details);

			const { result } = await this._finalizeCompaction({
				summary,
				firstKeptEntryId,
				tokensBefore,
				details,
				fromExtension,
			});

			return result;
		} finally {
			this._compactionAbortController = undefined;
			this._reconnectToAgent();
		}
	}

	/**
	 * Cancel in-progress compaction (manual or auto).
	 */
	abortCompaction(): void {
		this._compactionAbortController?.abort();
		this._autoCompactionAbortController?.abort();
	}

	/**
	 * Cancel in-progress branch summarization.
	 */
	abortBranchSummary(): void {
		this._branchSummaryAbortController?.abort();
	}

	/**
	 * Check if compaction is needed and run it.
	 * Called after agent_end and before prompt submission.
	 *
	 * Two cases:
	 * 1. Overflow: LLM returned context overflow error, remove error message from agent state, compact, auto-retry
	 * 2. Threshold: Context over threshold, compact, NO auto-retry (user continues manually)
	 *
	 * @param assistantMessage The assistant message to check
	 * @param skipAbortedCheck If false, include aborted messages (for pre-prompt check). Default: true
	 */
	private async _checkCompaction(assistantMessage: AssistantMessage, skipAbortedCheck = true): Promise<void> {
		const settings = this.settingsManager.getCompactionSettings();
		if (!settings.enabled) return;

		// Skip if message was aborted (user cancelled) - unless skipAbortedCheck is false
		if (skipAbortedCheck && assistantMessage.stopReason === "aborted") return;

		const contextWindow = this.model?.contextWindow ?? 0;

		// Skip overflow check if the message came from a different model.
		// This handles the case where user switched from a smaller-context model (e.g. opus)
		// to a larger-context model (e.g. codex) - the overflow error from the old model
		// shouldn't trigger compaction for the new model.
		const sameModel =
			this.model && assistantMessage.provider === this.model.provider && assistantMessage.model === this.model.id;

		// Skip compaction checks if this assistant message is older than the latest
		// compaction boundary. This prevents a stale pre-compaction usage/error
		// from retriggering compaction on the first prompt after compaction.
		const compactionEntry = getLatestCompactionEntry(this.sessionManager.getBranch());
		const assistantIsFromBeforeCompaction =
			compactionEntry !== null && assistantMessage.timestamp <= new Date(compactionEntry.timestamp).getTime();
		if (assistantIsFromBeforeCompaction) {
			return;
		}

		// Case 1: Overflow - LLM returned context overflow error
		if (sameModel && isContextOverflow(assistantMessage, contextWindow)) {
			if (this._overflowRecoveryCount >= MAX_OVERFLOW_RECOVERY_ATTEMPTS) {
				this._emit({
					type: "auto_compaction_end",
					result: undefined,
					aborted: false,
					willRetry: false,
					errorMessage:
						`Context overflow recovery failed after ${MAX_OVERFLOW_RECOVERY_ATTEMPTS} verified compact-and-retry attempts. ` +
						"The working set still exceeds the provider's effective context. Reduce tools, shrink the system prompt, or configure a larger context window.",
				});
				return;
			}

			// Wire the provider disagreement into the Context Governor's adaptive
			// safety: the next preflight govern applies a stricter budget and a
			// deterministic forced reduction, so the retry is genuinely smaller.
			const governor = this._contextGovernor;
			const diagnostics = governor?.lastDiagnostics;
			if (governor) {
				governor.recordOverflow({
					configuredContextWindow: contextWindow,
					estimatedInputTokens:
						diagnostics?.inputTokensAfter ?? estimateContextTokens(this.agent.state.messages).tokens,
					reservedOutputTokens: diagnostics?.capability.reservedOutputTokens ?? 0,
					safetyReserveTokens: diagnostics?.capability.safetyReserveTokens ?? 0,
					accountingMode: governor.getTokenAccountingMode(),
					providerError: assistantMessage.errorMessage,
					observedInputTokens:
						assistantMessage.usage && assistantMessage.usage.input + assistantMessage.usage.cacheRead > 0
							? assistantMessage.usage.input + assistantMessage.usage.cacheRead
							: undefined,
					observedContextWindow: contextWindow,
				});
			}

			this._overflowRecoveryCount += 1;
			// Remove the error message from agent state (it IS saved to session for history,
			// but we don't want it in context for the retry)
			const messages = this.agent.state.messages;
			if (messages.length > 0 && messages[messages.length - 1].role === "assistant") {
				this.agent.replaceMessages(messages.slice(0, -1));
			}
			await this._runAutoCompaction("overflow", true);
			return;
		}

		// Case 2: Threshold - context is getting large
		// For error messages (no usage data), estimate from last successful response.
		// This ensures sessions that hit persistent API errors (e.g. 529) can still compact.
		let contextTokens: number;
		if (assistantMessage.stopReason === "error") {
			const messages = this.agent.state.messages;
			const estimate = estimateContextTokens(messages);
			if (estimate.lastUsageIndex === null) return; // No usage data at all
			// Verify the usage source is post-compaction. Kept pre-compaction messages
			// have stale usage reflecting the old (larger) context and would falsely
			// trigger compaction right after one just finished.
			const usageMsg = messages[estimate.lastUsageIndex];
			if (
				compactionEntry &&
				usageMsg.role === "assistant" &&
				(usageMsg as AssistantMessage).timestamp <= new Date(compactionEntry.timestamp).getTime()
			) {
				return;
			}
			contextTokens = estimate.tokens;
		} else {
			contextTokens = calculateContextTokens(assistantMessage.usage);
		}
		if (shouldCompact(contextTokens, contextWindow, settings)) {
			await this._runAutoCompaction("threshold", false);
		}
	}

	/**
	 * Internal: Run auto-compaction with events.
	 */
	private async _runAutoCompaction(reason: "overflow" | "threshold", willRetry: boolean): Promise<void> {
		const settings = this.settingsManager.getCompactionSettings();

		this._emit({ type: "auto_compaction_start", reason });
		this._autoCompactionAbortController = new AbortController();

		try {
			if (!this.model) {
				this._emit({ type: "auto_compaction_end", result: undefined, aborted: false, willRetry: false });
				return;
			}

			const apiKey = await this._modelRegistry.getApiKey(this.model);
			if (!apiKey) {
				this._emit({ type: "auto_compaction_end", result: undefined, aborted: false, willRetry: false });
				return;
			}

			const pathEntries = this.sessionManager.getBranch();

			const preparation = prepareCompaction(pathEntries, settings);
			if (!preparation) {
				this._emit({ type: "auto_compaction_end", result: undefined, aborted: false, willRetry: false });
				return;
			}

			let extensionCompaction: CompactionResult | undefined;
			let fromExtension = false;

			if (this._extensionRunner?.hasHandlers("session_before_compact")) {
				const extensionResult = (await this._extensionRunner.emit({
					type: "session_before_compact",
					preparation,
					branchEntries: pathEntries,
					customInstructions: undefined,
					signal: this._autoCompactionAbortController.signal,
				})) as SessionBeforeCompactResult | undefined;

				if (extensionResult?.cancel) {
					this._emit({ type: "auto_compaction_end", result: undefined, aborted: true, willRetry: false });
					return;
				}

				if (extensionResult?.compaction) {
					extensionCompaction = extensionResult.compaction;
					fromExtension = true;
				}
			}

			let summary: string;
			let firstKeptEntryId: string;
			let tokensBefore: number;
			let details: unknown;

			if (extensionCompaction) {
				// Extension provided compaction content
				summary = extensionCompaction.summary;
				firstKeptEntryId = extensionCompaction.firstKeptEntryId;
				tokensBefore = extensionCompaction.tokensBefore;
				details = extensionCompaction.details;
			} else {
				// Generate compaction result
				const compactResult = await compact(
					preparation,
					this.model,
					apiKey,
					undefined,
					this._autoCompactionAbortController.signal,
				);
				summary = compactResult.summary;
				firstKeptEntryId = compactResult.firstKeptEntryId;
				tokensBefore = compactResult.tokensBefore;
				details = compactResult.details;
			}

			if (this._autoCompactionAbortController.signal.aborted) {
				this._emit({ type: "auto_compaction_end", result: undefined, aborted: true, willRetry: false });
				return;
			}
			details = createDeterministicCompactionDetails(preparation, details);

			const { result } = await this._finalizeCompaction({
				summary,
				firstKeptEntryId,
				tokensBefore,
				details,
				fromExtension,
			});
			this._emit({ type: "auto_compaction_end", result, aborted: false, willRetry });

			if (willRetry) {
				const messages = this.agent.state.messages;
				const lastMsg = messages[messages.length - 1];
				if (lastMsg?.role === "assistant" && (lastMsg as AssistantMessage).stopReason === "error") {
					this.agent.replaceMessages(messages.slice(0, -1));
				}

				setTimeout(() => {
					this.agent.continue().catch(() => {});
				}, 100);
			} else if (this.agent.hasQueuedMessages()) {
				// Auto-compaction can complete while follow-up/steering/custom messages are waiting.
				// Kick the loop so queued messages are actually delivered.
				setTimeout(() => {
					this.agent.continue().catch(() => {});
				}, 100);
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "compaction failed";
			this._emit({
				type: "auto_compaction_end",
				result: undefined,
				aborted: false,
				willRetry: false,
				errorMessage:
					reason === "overflow"
						? `Context overflow recovery failed: ${errorMessage}`
						: `Auto-compaction failed: ${errorMessage}`,
			});
		} finally {
			this._autoCompactionAbortController = undefined;
		}
	}

	/**
	 * Toggle auto-compaction setting.
	 */
	setAutoCompactionEnabled(enabled: boolean): void {
		this.settingsManager.setCompactionEnabled(enabled);
	}

	/** Whether auto-compaction is enabled */
	get autoCompactionEnabled(): boolean {
		return this.settingsManager.getCompactionEnabled();
	}

	async bindExtensions(bindings: ExtensionBindings): Promise<void> {
		if (bindings.uiContext !== undefined) {
			this._extensionUIContext = bindings.uiContext;
		}
		if (bindings.commandContextActions !== undefined) {
			this._extensionCommandContextActions = bindings.commandContextActions;
		}
		if (bindings.shutdownHandler !== undefined) {
			this._extensionShutdownHandler = bindings.shutdownHandler;
		}
		if (bindings.onError !== undefined) {
			this._extensionErrorListener = bindings.onError;
		}

		if (this._extensionRunner) {
			this._applyExtensionBindings(this._extensionRunner);
			await this._extensionRunner.emit({ type: "session_start" });
			await this.extendResourcesFromExtensions("startup");
		}
	}

	private async extendResourcesFromExtensions(reason: "startup" | "reload"): Promise<void> {
		if (!this._extensionRunner?.hasHandlers("resources_discover")) {
			return;
		}

		const { skillPaths, promptPaths, themePaths } = await this._extensionRunner.emitResourcesDiscover(
			this._cwd,
			reason,
		);

		if (skillPaths.length === 0 && promptPaths.length === 0 && themePaths.length === 0) {
			return;
		}

		const extensionPaths: ResourceExtensionPaths = {
			skillPaths: this.buildExtensionResourcePaths(skillPaths),
			promptPaths: this.buildExtensionResourcePaths(promptPaths),
			themePaths: this.buildExtensionResourcePaths(themePaths),
		};

		this._resourceLoader.extendResources(extensionPaths);
		this._baseSystemPrompt = this._rebuildSystemPrompt(this.getActiveToolNames());
		this._applyBaseSystemPrompt();
	}

	private buildExtensionResourcePaths(entries: Array<{ path: string; extensionPath: string }>): Array<{
		path: string;
		metadata: { source: string; scope: "temporary"; origin: "top-level"; baseDir?: string };
	}> {
		return entries.map((entry) => {
			const source = this.getExtensionSourceLabel(entry.extensionPath);
			const baseDir = entry.extensionPath.startsWith("<") ? undefined : dirname(entry.extensionPath);
			return {
				path: entry.path,
				metadata: {
					source,
					scope: "temporary",
					origin: "top-level",
					baseDir,
				},
			};
		});
	}

	private getExtensionSourceLabel(extensionPath: string): string {
		if (extensionPath.startsWith("<")) {
			return `extension:${extensionPath.replace(/[<>]/g, "")}`;
		}
		const base = basename(extensionPath);
		const name = base.replace(/\.(ts|js)$/, "");
		return `extension:${name}`;
	}

	private _applyExtensionBindings(runner: ExtensionRunner): void {
		runner.setUIContext(this._extensionUIContext);
		runner.bindCommandContext(this._extensionCommandContextActions);

		this._extensionErrorUnsubscriber?.();
		this._extensionErrorUnsubscriber = this._extensionErrorListener
			? runner.onError(this._extensionErrorListener)
			: undefined;
	}

	private _refreshCurrentModelFromRegistry(): void {
		const currentModel = this.model;
		if (!currentModel) {
			return;
		}

		const refreshedModel = this._modelRegistry.find(currentModel.provider, currentModel.id);
		if (!refreshedModel || refreshedModel === currentModel) {
			return;
		}

		this.agent.setModel(refreshedModel);
	}

	private _bindExtensionCore(runner: ExtensionRunner): void {
		const normalizeLocation = (source: string): SlashCommandLocation | undefined => {
			if (source === "user" || source === "project" || source === "path") {
				return source;
			}
			return undefined;
		};

		const reservedBuiltins = new Set(BUILTIN_SLASH_COMMANDS.map((command) => command.name));

		const getCommands = (): SlashCommandInfo[] => {
			const extensionCommands: SlashCommandInfo[] = runner
				.getRegisteredCommandsWithPaths()
				.filter(({ command }) => !reservedBuiltins.has(command.name))
				.map(({ command, extensionPath }) => ({
					name: command.name,
					description: command.description,
					source: "extension",
					path: extensionPath,
				}));

			const templates: SlashCommandInfo[] = this.promptTemplates.map((template) => ({
				name: template.name,
				description: template.description,
				source: "prompt",
				location: normalizeLocation(template.source),
				path: template.filePath,
			}));

			const skills: SlashCommandInfo[] = this._resourceLoader.getSkills().skills.map((skill) => ({
				name: `skill:${skill.name}`,
				description: skill.description,
				source: "skill",
				location: normalizeLocation(skill.source),
				path: skill.filePath,
			}));

			return [...extensionCommands, ...templates, ...skills];
		};

		runner.bindCore(
			{
				sendMessage: (message, options) => {
					this.sendCustomMessage(message, options).catch((err) => {
						runner.emitError({
							extensionPath: "<runtime>",
							event: "send_message",
							error: err instanceof Error ? err.message : String(err),
						});
					});
				},
				sendUserMessage: (content, options) => {
					this.sendUserMessage(content, options).catch((err) => {
						runner.emitError({
							extensionPath: "<runtime>",
							event: "send_user_message",
							error: err instanceof Error ? err.message : String(err),
						});
					});
				},
				appendEntry: (customType, data) => {
					this.sessionManager.appendCustomEntry(customType, data);
				},
				setSessionName: (name) => {
					this.sessionManager.appendSessionInfo(name);
				},
				getSessionName: () => {
					return this.sessionManager.getSessionName();
				},
				setLabel: (entryId, label) => {
					this.sessionManager.appendLabelChange(entryId, label);
				},
				getActiveTools: () => this.getActiveToolNames(),
				getAllTools: () => this.getAllTools(),
				setActiveTools: (toolNames) => this.setActiveToolsByName(toolNames),
				refreshTools: () => this._refreshToolRegistry(),
				getCommands,
				setModel: async (model) => {
					const key = await this.modelRegistry.getApiKey(model);
					if (!key) return false;
					await this.setModel(model);
					return true;
				},
				getThinkingLevel: () => this.thinkingLevel,
				setThinkingLevel: (level) => this.setThinkingLevel(level),
			},
			{
				getModel: () => this.model,
				isIdle: () => !this.isStreaming,
				abort: () => this.abort(),
				hasPendingMessages: () => this.pendingMessageCount > 0,
				shutdown: () => {
					this._extensionShutdownHandler?.();
				},
				getContextUsage: () => this.getContextUsage(),
				compact: (options) => {
					void (async () => {
						try {
							const result = await this.compact(options?.customInstructions);
							options?.onComplete?.(result);
						} catch (error) {
							const err = error instanceof Error ? error : new Error(String(error));
							options?.onError?.(err);
						}
					})();
				},
				getSystemPrompt: () => this.systemPrompt,
				getActiveMissionId: () => this.missionId,
			},
			{
				registerProvider: (name, config) => {
					this._modelRegistry.registerProvider(name, config);
					this._refreshCurrentModelFromRegistry();
				},
				unregisterProvider: (name) => {
					this._modelRegistry.unregisterProvider(name);
					this._refreshCurrentModelFromRegistry();
				},
			},
		);
	}

	private _refreshToolRegistry(options?: { activeToolNames?: string[]; includeAllExtensionTools?: boolean }): void {
		const previousRegistryNames = new Set(this._toolRegistry.keys());
		const previousActiveToolNames = this.getActiveToolNames();

		const registeredTools = this._extensionRunner?.getAllRegisteredTools() ?? [];
		const allCustomTools = [
			...registeredTools,
			...this._customTools.map((def) => ({ definition: def, extensionPath: "<sdk>" })),
		];
		this._toolPromptSnippets = new Map(
			allCustomTools
				.map((registeredTool) => {
					const snippet = this._normalizePromptSnippet(registeredTool.definition.promptSnippet);
					return snippet ? ([registeredTool.definition.name, snippet] as const) : undefined;
				})
				.filter((entry): entry is readonly [string, string] => entry !== undefined),
		);
		this._toolPromptGuidelines = new Map(
			allCustomTools
				.map((registeredTool) => {
					const guidelines = this._normalizePromptGuidelines(registeredTool.definition.promptGuidelines);
					return guidelines.length > 0 ? ([registeredTool.definition.name, guidelines] as const) : undefined;
				})
				.filter((entry): entry is readonly [string, string[]] => entry !== undefined),
		);
		const wrappedExtensionTools = this._extensionRunner
			? wrapRegisteredTools(allCustomTools, this._extensionRunner)
			: [];

		const toolRegistry = new Map(this._baseToolRegistry);
		for (const tool of wrappedExtensionTools as AgentTool[]) {
			toolRegistry.set(tool.name, tool);
		}
		this._toolRegistry = toolRegistry;

		const nextActiveToolNames = options?.activeToolNames
			? [...options.activeToolNames]
			: [...previousActiveToolNames];

		if (options?.includeAllExtensionTools) {
			for (const tool of wrappedExtensionTools) {
				nextActiveToolNames.push(tool.name);
			}
		} else if (!options?.activeToolNames) {
			for (const toolName of this._toolRegistry.keys()) {
				if (!previousRegistryNames.has(toolName)) {
					nextActiveToolNames.push(toolName);
				}
			}
		}

		this.setActiveToolsByName([...new Set(nextActiveToolNames)]);
	}

	private _buildRuntime(options: {
		activeToolNames?: string[];
		flagValues?: Map<string, boolean | string>;
		includeAllExtensionTools?: boolean;
	}): void {
		const autoResizeImages = this.settingsManager.getImageAutoResize();
		const shellCommandPrefix = this.settingsManager.getShellCommandPrefix();
		const baseTools = this._baseToolsOverride
			? this._baseToolsOverride
			: createAllTools(this._cwd, {
					read: { autoResizeImages },
					bash: { commandPrefix: shellCommandPrefix },
				});

		this._baseToolRegistry = new Map(Object.entries(baseTools).map(([name, tool]) => [name, tool as AgentTool]));

		// Create todo_write, todo_read, and todo_update tools with session callbacks
		const todoWriteTool = createTodoWriteTool(
			() => this._todos,
			(todos) => this._setTodos(todos),
			this._todoLoopGuard,
			() => this._todoRevision,
			this._todoPerTurnLock,
		);
		const todoReadTool = createTodoReadTool(
			() => this._todos,
			() => this._todoRevision,
			{
				onRead: () => {
					this._todoReadSnapshot = {
						revision: this._todoRevision,
						timestamp: Date.now(),
					};
					this._todoUpdateRejectionState.count = 0;
				},
			},
			this._todoEngine,
		);
		const todoUpdateTool = createTodoUpdateTool(
			() => this._todos,
			(todos) => this._setTodos(todos),
			() => this._todoRevision,
			this._todoLoopGuard,
			{
				getSnapshot: () => this._todoReadSnapshot,
				invalidateSnapshot: () => {
					this._todoReadSnapshot = null;
				},
			},
			this._todoUpdateRejectionState,
			this._todoEngine,
		);
		this._baseToolRegistry.set("todo_write", todoWriteTool as unknown as AgentTool);
		this._baseToolRegistry.set("todo_read", todoReadTool as unknown as AgentTool);
		this._baseToolRegistry.set("todo_update", todoUpdateTool as unknown as AgentTool);

		const memoryWriteTool = createMemoryWriteTool({
			set: (key, value) => this.setMemoryItem(key, value),
			clear: () => this.clearMemory(),
		});
		this._baseToolRegistry.set("memory_write", memoryWriteTool as unknown as AgentTool);

		// Model-facing evidence rehydration (2.5.0 extension). Retrieves bounded,
		// integrity-verified pages of cold evidence by id. Read-only; does not
		// move completion authority. Registered only when a session archive is
		// provided (the SDK always provides one for governed sessions).
		if (this._evidenceArchive) {
			const retrieveEvidenceTool = createRetrieveEvidenceTool(this._evidenceArchive);
			this._baseToolRegistry.set("retrieve_evidence", retrieveEvidenceTool as unknown as AgentTool);
		}

		// Create task tools with session callbacks
		const taskCreateTool = createTaskCreateTool(
			() => this._tasks,
			(tasks) => this._setTasks(tasks),
		);
		const taskListTool = createTaskListTool(
			() => this._tasks,
			(tasks) => this._setTasks(tasks),
		);
		const taskGetTool = createTaskGetTool(
			() => this._tasks,
			(tasks) => this._setTasks(tasks),
		);
		const taskUpdateTool = createTaskUpdateTool(
			() => this._tasks,
			(tasks) => this._setTasks(tasks),
		);
		this._baseToolRegistry.set("task_create", taskCreateTool as unknown as AgentTool);
		this._baseToolRegistry.set("task_list", taskListTool as unknown as AgentTool);
		this._baseToolRegistry.set("task_get", taskGetTool as unknown as AgentTool);
		this._baseToolRegistry.set("task_update", taskUpdateTool as unknown as AgentTool);

		const extensionsResult = this._resourceLoader.getExtensions();
		if (options.flagValues) {
			for (const [name, value] of options.flagValues) {
				extensionsResult.runtime.flagValues.set(name, value);
			}
		}

		const hasExtensions = extensionsResult.extensions.length > 0;
		const hasCustomTools = this._customTools.length > 0;
		this._extensionRunner =
			hasExtensions || hasCustomTools
				? new ExtensionRunner(
						extensionsResult.extensions,
						extensionsResult.runtime,
						this._cwd,
						this.sessionManager,
						this._modelRegistry,
					)
				: undefined;
		if (this._extensionRunnerRef) {
			this._extensionRunnerRef.current = this._extensionRunner;
		}
		if (this._extensionRunner) {
			this._bindExtensionCore(this._extensionRunner);
			this._applyExtensionBindings(this._extensionRunner);
		}

		const defaultActiveToolNames = this._baseToolsOverride
			? Object.keys(this._baseToolsOverride)
			: [
					"read",
					"bash",
					"edit",
					"write",
					"todo_write",
					"todo_read",
					"todo_update",
					"memory_write",
					"retrieve_evidence",
					"web_search",
					"web_fetch",
					"deep_research",
					"web_research_status",
				];
		const baseActiveToolNames = options.activeToolNames ?? defaultActiveToolNames;
		this._refreshToolRegistry({
			activeToolNames: baseActiveToolNames,
			includeAllExtensionTools: options.includeAllExtensionTools,
		});
	}

	async reload(): Promise<void> {
		const previousFlagValues = this._extensionRunner?.getFlagValues();
		await this._extensionRunner?.emit({ type: "session_shutdown" });
		this.settingsManager.reload();
		resetApiProviders();
		await this._resourceLoader.reload();
		this._buildRuntime({
			activeToolNames: this.getActiveToolNames(),
			flagValues: previousFlagValues,
			includeAllExtensionTools: true,
		});

		const hasBindings =
			this._extensionUIContext ||
			this._extensionCommandContextActions ||
			this._extensionShutdownHandler ||
			this._extensionErrorListener;
		if (this._extensionRunner && hasBindings) {
			await this._extensionRunner.emit({ type: "session_start" });
			await this.extendResourcesFromExtensions("reload");
		}
	}

	// =========================================================================
	// Auto-Retry
	// =========================================================================

	/**
	 * Check if an error is retryable (overloaded, rate limit, server errors).
	 * Context overflow errors are NOT retryable (handled by compaction instead).
	 * Transcript structure errors are NOT retryable (same payload would fail again).
	 */
	private _isRetryableError(message: AssistantMessage): boolean {
		if (message.stopReason !== "error" || !message.errorMessage) return false;

		// Context overflow is handled by compaction, not retry
		const contextWindow = this.model?.contextWindow ?? 0;
		if (isContextOverflow(message, contextWindow)) return false;

		const err = message.errorMessage;

		// Non-retryable transcript errors: deterministic failures from invalid tool call/result structure
		// These fail with 400 and the same payload would produce the same error again
		if (
			/INVALID_TOOL_TRANSCRIPT/i.test(err) ||
			/must be followed by tool messages/i.test(err) ||
			/invalid_request_error/i.test(err) ||
			/Invalid parameter/i.test(err)
		) {
			return false;
		}

		// Match: overloaded_error, provider returned error, rate limit, 429, 500, 502, 503, 504, service unavailable, network/connection errors, fetch failed, terminated, retry delay exceeded
		return /overloaded|provider.?returned.?error|rate.?limit|too many requests|429|500|502|503|504|service.?unavailable|server.?error|internal.?error|network.?error|connection.?error|connection.?refused|other side closed|fetch failed|upstream.?connect|reset before headers|socket hang up|timed? out|timeout|terminated|retry delay/i.test(
			err,
		);
	}

	/**
	 * Handle retryable errors with exponential backoff.
	 * @returns true if retry was initiated, false if max retries exceeded or disabled
	 */
	private async _handleRetryableError(message: AssistantMessage): Promise<boolean> {
		const settings = this.settingsManager.getRetrySettings();
		if (!settings.enabled) {
			this._resolveRetry();
			return false;
		}

		// Retry promise is created synchronously in _handleAgentEvent for agent_end.
		// Keep a defensive fallback here in case a future refactor bypasses that path.
		if (!this._retryPromise) {
			this._retryPromise = new Promise((resolve) => {
				this._retryResolve = resolve;
			});
		}

		this._retryAttempt++;

		if (this._retryAttempt > settings.maxRetries) {
			// Try fallback before completely failing
			if (this._fallbackModels.length > 0 && this._fallbackIndex < this._fallbackModels.length - 1) {
				const didFallback = await this._handleFallback(message, message.errorMessage || "Max retries exceeded");
				if (didFallback) {
					this._resolveRetry(); // Resolve so waitForRetry() completes
					return true;
				}
			}

			// Max retries exceeded, emit final failure and reset
			this._emit({
				type: "auto_retry_end",
				success: false,
				attempt: this._retryAttempt - 1,
				finalError: message.errorMessage,
			});
			this._retryAttempt = 0;
			this._resolveRetry(); // Resolve so waitForRetry() completes
			return false;
		}

		const delayMs = settings.baseDelayMs * 2 ** (this._retryAttempt - 1);

		this._emit({
			type: "auto_retry_start",
			attempt: this._retryAttempt,
			maxAttempts: settings.maxRetries,
			delayMs,
			errorMessage: message.errorMessage || "Unknown error",
		});

		// Remove error message from agent state (keep in session for history)
		const messages = this.agent.state.messages;
		if (messages.length > 0 && messages[messages.length - 1].role === "assistant") {
			this.agent.replaceMessages(messages.slice(0, -1));
		}

		// Wait with exponential backoff (abortable)
		this._retryAbortController = new AbortController();
		try {
			await sleep(delayMs, this._retryAbortController.signal);
		} catch {
			// Aborted during sleep - emit end event so UI can clean up
			const attempt = this._retryAttempt;
			this._retryAttempt = 0;
			this._retryAbortController = undefined;
			this._emit({
				type: "auto_retry_end",
				success: false,
				attempt,
				finalError: "Retry cancelled",
			});
			this._resolveRetry();
			return false;
		}
		this._retryAbortController = undefined;

		// Retry via continue() - use setTimeout to break out of event handler chain
		setTimeout(() => {
			this.agent.continue().catch(() => {
				// Retry failed - will be caught by next agent_end
			});
		}, 0);

		return true;
	}

	private async _handleFallback(message: AssistantMessage, reason: string): Promise<boolean> {
		if (this._fallbackIndex >= this._fallbackModels.length - 1) {
			return false; // Exhausted
		}

		this._fallbackIndex++;
		const fallbackModel = this._fallbackModels[this._fallbackIndex];

		// Ensure api key exists for fallback
		const apiKey = await this._modelRegistry.getApiKey(fallbackModel);
		if (!apiKey) {
			// API key missing, try next fallback immediately recursively
			return this._handleFallback(message, `${reason} (Skipped ${fallbackModel.id}: missing API key)`);
		}

		this._emit({
			type: "auto_fallback",
			model: this.model!,
			fallbackModel: fallbackModel,
			reason,
		});

		// Switch model
		await this.setModel(fallbackModel);

		// Remove error message from agent state (keep in session for history)
		const messages = this.agent.state.messages;
		if (messages.length > 0 && messages[messages.length - 1].role === "assistant") {
			this.agent.replaceMessages(messages.slice(0, -1));
		}

		// We reset the retry attempt for the new model
		this._retryAttempt = 0;

		// Restart via continue()
		setTimeout(() => {
			this.agent.continue().catch(() => {});
		}, 0);

		return true;
	}

	/**
	 * Cancel in-progress retry.
	 */
	abortRetry(): void {
		this._retryAbortController?.abort();
		// Note: _retryAttempt is reset in the catch block of _autoRetry
		this._resolveRetry();
	}

	/**
	 * Wait for any in-progress retry to complete.
	 * Returns immediately if no retry is in progress.
	 */
	private async waitForRetry(): Promise<void> {
		if (this._retryPromise) {
			await this._retryPromise;
		}
	}

	/** Whether auto-retry is currently in progress */
	get isRetrying(): boolean {
		return this._retryPromise !== undefined;
	}

	/** Whether auto-retry is enabled */
	get autoRetryEnabled(): boolean {
		return this.settingsManager.getRetryEnabled();
	}

	/**
	 * Toggle auto-retry setting.
	 */
	setAutoRetryEnabled(enabled: boolean): void {
		this.settingsManager.setRetryEnabled(enabled);
	}

	// =========================================================================
	// Bash Execution
	// =========================================================================

	/**
	 * Execute a bash command.
	 * Adds result to agent context and session.
	 * @param command The bash command to execute
	 * @param onChunk Optional streaming callback for output
	 * @param options.excludeFromContext If true, command output won't be sent to LLM (!! prefix)
	 * @param options.operations Custom BashOperations for remote execution
	 */
	async executeBash(
		command: string,
		onChunk?: (chunk: string) => void,
		options?: { excludeFromContext?: boolean; operations?: BashOperations },
	): Promise<BashResult> {
		this._bashAbortController = new AbortController();

		// Apply command prefix if configured (e.g., "shopt -s expand_aliases" for alias support)
		const prefix = this.settingsManager.getShellCommandPrefix();
		const resolvedCommand = prefix ? `${prefix}\n${command}` : command;

		try {
			const result = options?.operations
				? await executeBashWithOperations(resolvedCommand, process.cwd(), options.operations, {
						onChunk,
						signal: this._bashAbortController.signal,
					})
				: await executeBashCommand(resolvedCommand, {
						onChunk,
						signal: this._bashAbortController.signal,
					});

			this.recordBashResult(command, result, options);
			return result;
		} finally {
			this._bashAbortController = undefined;
		}
	}

	/**
	 * Record a bash execution result in session history.
	 * Used by executeBash and by extensions that handle bash execution themselves.
	 */
	recordBashResult(command: string, result: BashResult, options?: { excludeFromContext?: boolean }): void {
		const bashMessage: BashExecutionMessage = {
			role: "bashExecution",
			command,
			output: result.output,
			exitCode: result.exitCode,
			cancelled: result.cancelled,
			truncated: result.truncated,
			fullOutputPath: result.fullOutputPath,
			timestamp: Date.now(),
			excludeFromContext: options?.excludeFromContext,
		};

		// If agent is streaming, defer adding to avoid breaking tool_use/tool_result ordering
		if (this.isStreaming) {
			// Queue for later - will be flushed on agent_end
			this._pendingBashMessages.push(bashMessage);
		} else {
			// Add to agent state immediately
			this.agent.appendMessage(bashMessage);

			// Save to session
			this.sessionManager.appendMessage(bashMessage);
		}
	}

	/**
	 * Cancel running bash command.
	 */
	abortBash(): void {
		this._bashAbortController?.abort();
	}

	/** Whether a bash command is currently running */
	get isBashRunning(): boolean {
		return this._bashAbortController !== undefined;
	}

	/** Whether there are pending bash messages waiting to be flushed */
	get hasPendingBashMessages(): boolean {
		return this._pendingBashMessages.length > 0;
	}

	/**
	 * Flush pending bash messages to agent state and session.
	 * Called after agent turn completes to maintain proper message ordering.
	 */
	private _flushPendingBashMessages(): void {
		if (this._pendingBashMessages.length === 0) return;

		for (const bashMessage of this._pendingBashMessages) {
			// Add to agent state
			this.agent.appendMessage(bashMessage);

			// Save to session
			this.sessionManager.appendMessage(bashMessage);
		}

		this._pendingBashMessages = [];
	}

	// =========================================================================
	// Session Management
	// =========================================================================

	/**
	 * Switch to a different session file.
	 * Aborts current operation, loads messages, restores model/thinking.
	 * Listeners are preserved and will continue receiving events.
	 * @returns true if switch completed, false if cancelled by extension
	 */
	async switchSession(sessionPath: string): Promise<boolean> {
		const previousSessionFile = this.sessionManager.getSessionFile();

		// Emit session_before_switch event (can be cancelled)
		if (this._extensionRunner?.hasHandlers("session_before_switch")) {
			const result = (await this._extensionRunner.emit({
				type: "session_before_switch",
				reason: "resume",
				targetSessionFile: sessionPath,
			})) as SessionBeforeSwitchResult | undefined;

			if (result?.cancel) {
				return false;
			}
		}

		this._disconnectFromAgent();
		await this.abort();
		this._steeringMessages = [];
		this._followUpMessages = [];
		this._pendingNextTurnMessages = [];
		this._cavemanLevel = "off";
		this._briefOnly = false;

		// Set new session
		this.sessionManager.setSessionFile(sessionPath);
		this.agent.sessionId = this.sessionManager.getSessionId();

		// Reload messages
		const sessionContext = this.sessionManager.buildSessionContext();
		this._todos = sessionContext.todos as TodoItem[];
		this._resetTodoEngine();
		this._todoRevision = this._todoEngine.getCurrentRevision();
		this._todoReadSnapshot = null;
		this._todoUpdateRejectionState.count = 0;
		this._todoPerTurnLock.currentTurn = 0;
		this._todoPerTurnLock.lockedUntil = -1;
		this._todoPerTurnLock.reservedUntil = -1;
		this._memoryItems = sessionContext.memoryItems;
		this._delegatedTasks = [];
		this._tasks = this.sessionManager.getLatestSessionTasks();

		// Rebuild system prompt after caveman/brief reset
		this._baseSystemPrompt = this._rebuildSystemPrompt(this.getActiveToolNames());
		this._emit({ type: "todo_update", todos: this._todos });
		this._emit({ type: "memory_update", items: this._memoryItems });
		this._emit({ type: "task_update", tasks: this._tasks });

		// Emit session_switch event to extensions
		if (this._extensionRunner) {
			await this._extensionRunner.emit({
				type: "session_switch",
				reason: "resume",
				previousSessionFile,
			});
		}

		// Emit session event to custom tools

		this.agent.replaceMessages(sessionContext.messages);

		// Apply rebuilt system prompt after message restoration
		this._applyBaseSystemPrompt();

		// Restore model if saved
		if (sessionContext.model) {
			const previousModel = this.model;
			const availableModels = await this._modelRegistry.getAvailable();
			const match = availableModels.find(
				(m) => m.provider === sessionContext.model!.provider && m.id === sessionContext.model!.modelId,
			);
			if (match) {
				this.agent.setModel(match);
				await this._emitModelSelect(match, previousModel, "restore");
			}
		}

		const hasThinkingEntry = this.sessionManager.getBranch().some((entry) => entry.type === "thinking_level_change");
		const defaultThinkingLevel = this.settingsManager.getDefaultThinkingLevel() ?? DEFAULT_THINKING_LEVEL;

		if (hasThinkingEntry) {
			// Restore thinking level if saved (setThinkingLevel clamps to model capabilities)
			this.setThinkingLevel(sessionContext.thinkingLevel as ThinkingLevel);
		} else {
			const availableLevels = this.getAvailableThinkingLevels();
			const effectiveLevel = availableLevels.includes(defaultThinkingLevel)
				? defaultThinkingLevel
				: this._clampThinkingLevel(defaultThinkingLevel, availableLevels);
			this.agent.setThinkingLevel(effectiveLevel);
			this.sessionManager.appendThinkingLevelChange(effectiveLevel);
		}

		this._reconnectToAgent();
		return true;
	}

	/**
	 * Set a display name for the current session.
	 */
	setSessionName(name: string): void {
		this.sessionManager.appendSessionInfo(name);
	}

	/**
	 * Create a fork from a specific entry.
	 * Emits before_fork/fork session events to extensions.
	 *
	 * @param entryId ID of the entry to fork from
	 * @returns Object with:
	 *   - selectedText: The text of the selected user message (for editor pre-fill)
	 *   - cancelled: True if an extension cancelled the fork
	 */
	async fork(entryId: string): Promise<{ selectedText: string; cancelled: boolean }> {
		const previousSessionFile = this.sessionFile;
		const selectedEntry = this.sessionManager.getEntry(entryId);

		if (!selectedEntry || selectedEntry.type !== "message" || selectedEntry.message.role !== "user") {
			throw new Error("Invalid entry ID for forking");
		}

		const selectedText = this._extractUserMessageText(selectedEntry.message.content);

		let skipConversationRestore = false;

		// Emit session_before_fork event (can be cancelled)
		if (this._extensionRunner?.hasHandlers("session_before_fork")) {
			const result = (await this._extensionRunner.emit({
				type: "session_before_fork",
				entryId,
			})) as SessionBeforeForkResult | undefined;

			if (result?.cancel) {
				return { selectedText, cancelled: true };
			}
			skipConversationRestore = result?.skipConversationRestore ?? false;
		}

		// Clear pending messages (bound to old session state)
		this._pendingNextTurnMessages = [];
		this._cavemanLevel = "off";
		this._briefOnly = false;

		if (!selectedEntry.parentId) {
			this.sessionManager.newSession({ parentSession: previousSessionFile });
		} else {
			this.sessionManager.createBranchedSession(selectedEntry.parentId);
		}
		this.agent.sessionId = this.sessionManager.getSessionId();

		// Reload messages from entries (works for both file and in-memory mode)
		const sessionContext = this.sessionManager.buildSessionContext();

		// Rebuild system prompt after caveman/brief reset
		this._baseSystemPrompt = this._rebuildSystemPrompt(this.getActiveToolNames());

		// Emit session_fork event to extensions (after fork completes)
		if (this._extensionRunner) {
			await this._extensionRunner.emit({
				type: "session_fork",
				previousSessionFile,
			});
		}

		// Emit session event to custom tools (with reason "fork")

		if (!skipConversationRestore) {
			this.agent.replaceMessages(sessionContext.messages);
			this._applyBaseSystemPrompt();
		}

		return { selectedText, cancelled: false };
	}

	// =========================================================================
	// Tree Navigation
	// =========================================================================

	/**
	 * Navigate to a different node in the session tree.
	 * Unlike fork() which creates a new session file, this stays in the same file.
	 *
	 * @param targetId The entry ID to navigate to
	 * @param options.summarize Whether user wants to summarize abandoned branch
	 * @param options.customInstructions Custom instructions for summarizer
	 * @param options.replaceInstructions If true, customInstructions replaces the default prompt
	 * @param options.label Label to attach to the branch summary entry
	 * @returns Result with editorText (if user message) and cancelled status
	 */
	async navigateTree(
		targetId: string,
		options: { summarize?: boolean; customInstructions?: string; replaceInstructions?: boolean; label?: string } = {},
	): Promise<{ editorText?: string; cancelled: boolean; aborted?: boolean; summaryEntry?: BranchSummaryEntry }> {
		const oldLeafId = this.sessionManager.getLeafId();

		// No-op if already at target
		if (targetId === oldLeafId) {
			return { cancelled: false };
		}

		// Model required for summarization
		if (options.summarize && !this.model) {
			throw new Error("No model available for summarization");
		}

		const targetEntry = this.sessionManager.getEntry(targetId);
		if (!targetEntry) {
			throw new Error(`Entry ${targetId} not found`);
		}

		// Collect entries to summarize (from old leaf to common ancestor)
		const { entries: entriesToSummarize, commonAncestorId } = collectEntriesForBranchSummary(
			this.sessionManager,
			oldLeafId,
			targetId,
		);

		// Prepare event data - mutable so extensions can override
		let customInstructions = options.customInstructions;
		let replaceInstructions = options.replaceInstructions;
		let label = options.label;

		const preparation: TreePreparation = {
			targetId,
			oldLeafId,
			commonAncestorId,
			entriesToSummarize,
			userWantsSummary: options.summarize ?? false,
			customInstructions,
			replaceInstructions,
			label,
		};

		// Set up abort controller for summarization
		this._branchSummaryAbortController = new AbortController();
		let extensionSummary: { summary: string; details?: unknown } | undefined;
		let fromExtension = false;

		// Emit session_before_tree event
		if (this._extensionRunner?.hasHandlers("session_before_tree")) {
			const result = (await this._extensionRunner.emit({
				type: "session_before_tree",
				preparation,
				signal: this._branchSummaryAbortController.signal,
			})) as SessionBeforeTreeResult | undefined;

			if (result?.cancel) {
				return { cancelled: true };
			}

			if (result?.summary && options.summarize) {
				extensionSummary = result.summary;
				fromExtension = true;
			}

			// Allow extensions to override instructions and label
			if (result?.customInstructions !== undefined) {
				customInstructions = result.customInstructions;
			}
			if (result?.replaceInstructions !== undefined) {
				replaceInstructions = result.replaceInstructions;
			}
			if (result?.label !== undefined) {
				label = result.label;
			}
		}

		// Run default summarizer if needed
		let summaryText: string | undefined;
		let summaryDetails: unknown;
		if (options.summarize && entriesToSummarize.length > 0 && !extensionSummary) {
			const model = this.model!;
			const apiKey = await this._modelRegistry.getApiKey(model);
			if (!apiKey) {
				throw new Error(`No API key for ${model.provider}`);
			}
			const branchSummarySettings = this.settingsManager.getBranchSummarySettings();
			const result = await generateBranchSummary(entriesToSummarize, {
				model,
				apiKey,
				signal: this._branchSummaryAbortController.signal,
				customInstructions,
				replaceInstructions,
				reserveTokens: branchSummarySettings.reserveTokens,
			});
			this._branchSummaryAbortController = undefined;
			if (result.aborted) {
				return { cancelled: true, aborted: true };
			}
			if (result.error) {
				throw new Error(result.error);
			}
			summaryText = result.summary;
			summaryDetails = {
				readFiles: result.readFiles || [],
				modifiedFiles: result.modifiedFiles || [],
			};
		} else if (extensionSummary) {
			summaryText = extensionSummary.summary;
			summaryDetails = extensionSummary.details;
		}

		// Determine the new leaf position based on target type
		let newLeafId: string | null;
		let editorText: string | undefined;

		if (targetEntry.type === "message" && targetEntry.message.role === "user") {
			// User message: leaf = parent (null if root), text goes to editor
			newLeafId = targetEntry.parentId;
			editorText = this._extractUserMessageText(targetEntry.message.content);
		} else if (targetEntry.type === "custom_message") {
			// Custom message: leaf = parent (null if root), text goes to editor
			newLeafId = targetEntry.parentId;
			editorText =
				typeof targetEntry.content === "string"
					? targetEntry.content
					: targetEntry.content
							.filter((c): c is { type: "text"; text: string } => c.type === "text")
							.map((c) => c.text)
							.join("");
		} else {
			// Non-user message: leaf = selected node
			newLeafId = targetId;
		}

		// Switch leaf (with or without summary)
		// Summary is attached at the navigation target position (newLeafId), not the old branch
		let summaryEntry: BranchSummaryEntry | undefined;
		if (summaryText) {
			// Create summary at target position (can be null for root)
			const summaryId = this.sessionManager.branchWithSummary(newLeafId, summaryText, summaryDetails, fromExtension);
			summaryEntry = this.sessionManager.getEntry(summaryId) as BranchSummaryEntry;

			// Attach label to the summary entry
			if (label) {
				this.sessionManager.appendLabelChange(summaryId, label);
			}
		} else if (newLeafId === null) {
			// No summary, navigating to root - reset leaf
			this.sessionManager.resetLeaf();
		} else {
			// No summary, navigating to non-root
			this.sessionManager.branch(newLeafId);
		}

		// Attach label to target entry when not summarizing (no summary entry to label)
		if (label && !summaryText) {
			this.sessionManager.appendLabelChange(targetId, label);
		}

		// Update agent state
		const sessionContext = this.sessionManager.buildSessionContext();
		this.agent.replaceMessages(sessionContext.messages);

		// Emit session_tree event
		if (this._extensionRunner) {
			await this._extensionRunner.emit({
				type: "session_tree",
				newLeafId: this.sessionManager.getLeafId(),
				oldLeafId,
				summaryEntry,
				fromExtension: summaryText ? fromExtension : undefined,
			});
		}

		// Emit to custom tools

		this._branchSummaryAbortController = undefined;
		return { editorText, cancelled: false, summaryEntry };
	}

	/**
	 * Get all user messages from session for fork selector.
	 */
	getUserMessagesForForking(): Array<{ entryId: string; text: string }> {
		const entries = this.sessionManager.getEntries();
		const result: Array<{ entryId: string; text: string }> = [];

		for (const entry of entries) {
			if (entry.type !== "message") continue;
			if (entry.message.role !== "user") continue;

			const text = this._extractUserMessageText(entry.message.content);
			if (text) {
				result.push({ entryId: entry.id, text });
			}
		}

		return result;
	}

	private _extractUserMessageText(content: string | Array<{ type: string; text?: string }>): string {
		if (typeof content === "string") return content;
		if (Array.isArray(content)) {
			return content
				.filter((c): c is { type: "text"; text: string } => c.type === "text")
				.map((c) => c.text)
				.join("");
		}
		return "";
	}

	/**
	 * Get session statistics.
	 */
	getSessionStats(): SessionStats {
		const state = this.state;
		const userMessages = state.messages.filter((m) => m.role === "user").length;
		const assistantMessages = state.messages.filter((m) => m.role === "assistant").length;
		const toolResults = state.messages.filter((m) => m.role === "toolResult").length;

		let toolCalls = 0;
		let totalInput = 0;
		let totalOutput = 0;
		let totalCacheRead = 0;
		let totalCacheWrite = 0;
		let totalCost = 0;

		for (const message of state.messages) {
			if (message.role === "assistant") {
				const assistantMsg = message as AssistantMessage;
				toolCalls += assistantMsg.content.filter((c) => c.type === "toolCall").length;
				totalInput += assistantMsg.usage.input;
				totalOutput += assistantMsg.usage.output;
				totalCacheRead += assistantMsg.usage.cacheRead;
				totalCacheWrite += assistantMsg.usage.cacheWrite;
				totalCost += assistantMsg.usage.cost.total;
			}
		}

		return {
			sessionFile: this.sessionFile,
			sessionId: this.sessionId,
			userMessages,
			assistantMessages,
			toolCalls,
			toolResults,
			totalMessages: state.messages.length,
			tokens: {
				input: totalInput,
				output: totalOutput,
				cacheRead: totalCacheRead,
				cacheWrite: totalCacheWrite,
				total: totalInput + totalOutput + totalCacheRead + totalCacheWrite,
			},
			cost: totalCost,
			contextCache: state.contextCache,
		};
	}

	getContextUsage(): ContextUsage | undefined {
		const model = this.model;
		if (!model) return undefined;

		const contextWindow = model.contextWindow ?? 0;
		if (contextWindow <= 0) return undefined;

		// After compaction, the last assistant usage reflects pre-compaction context size.
		// We can only trust usage from an assistant that responded after the latest compaction.
		// If no such assistant exists, context token count is unknown until the next LLM response.
		const branchEntries = this.sessionManager.getBranch();
		const latestCompaction = getLatestCompactionEntry(branchEntries);

		if (latestCompaction) {
			// Check if there's a valid assistant usage after the compaction boundary
			const compactionIndex = branchEntries.lastIndexOf(latestCompaction);
			let hasPostCompactionUsage = false;
			for (let i = branchEntries.length - 1; i > compactionIndex; i--) {
				const entry = branchEntries[i];
				if (entry.type === "message" && entry.message.role === "assistant") {
					const assistant = entry.message;
					if (assistant.stopReason !== "aborted" && assistant.stopReason !== "error") {
						const contextTokens = calculateContextTokens(assistant.usage);
						if (contextTokens > 0) {
							hasPostCompactionUsage = true;
						}
						break;
					}
				}
			}

			if (!hasPostCompactionUsage) {
				return { tokens: null, contextWindow, percent: null };
			}
		}

		const estimate = estimateContextTokens(this.messages);
		const percent = (estimate.tokens / contextWindow) * 100;

		return {
			tokens: estimate.tokens,
			contextWindow,
			percent,
		};
	}

	/**
	 * Export session to HTML.
	 * @param outputPath Optional output path (defaults to session directory)
	 * @returns Path to exported file
	 */
	async exportToHtml(outputPath?: string): Promise<string> {
		const themeName = this.settingsManager.getTheme();

		// Create tool renderer if we have an extension runner (for custom tool HTML rendering)
		let toolRenderer: ToolHtmlRenderer | undefined;
		if (this._extensionRunner) {
			toolRenderer = createToolHtmlRenderer({
				getToolDefinition: (name) => this._extensionRunner!.getToolDefinition(name),
				theme,
			});
		}

		return await exportSessionToHtml(this.sessionManager, this.state, {
			outputPath,
			themeName,
			toolRenderer,
		});
	}

	// =========================================================================
	// Utilities
	// =========================================================================

	/**
	 * Get text content of last assistant message.
	 * Useful for /copy command.
	 * @returns Text content, or undefined if no assistant message exists
	 */
	getLastAssistantText(): string | undefined {
		const lastAssistant = this.messages
			.slice()
			.reverse()
			.find((m) => {
				if (m.role !== "assistant") return false;
				const msg = m as AssistantMessage;
				// Skip aborted messages with no content
				if (msg.stopReason === "aborted" && msg.content.length === 0) return false;
				return true;
			});

		if (!lastAssistant) return undefined;

		let text = "";
		for (const content of (lastAssistant as AssistantMessage).content) {
			if (content.type === "text") {
				text += content.text;
			}
		}

		return text.trim() || undefined;
	}

	// =========================================================================
	// Extension System
	// =========================================================================

	/**
	 * Check if extensions have handlers for a specific event type.
	 */
	hasExtensionHandlers(eventType: string): boolean {
		return this._extensionRunner?.hasHandlers(eventType) ?? false;
	}

	/**
	 * Get the extension runner (for setting UI context and error handlers).
	 */
	get extensionRunner(): ExtensionRunner | undefined {
		return this._extensionRunner;
	}
}
